// The canonical model, on the browser's side of the wire.
//
// `../../docs/json-format.md` is the specification and `canonical.py` is
// the other implementation of it. This file holds the types and the one
// function that turns a document into what the canvas draws.
//
// WHAT IS AND IS NOT CANONICAL, on this side. The document is the boundary
// format: it is what the API returns, what the stream carries, what a saved
// project holds and what an uploaded file contains. `SolvedPlan` and
// `ApartmentLevel` are the UI's own view models — internal to the renderer, the
// same way the solver's dicts are internal to the solver. Keeping that line is
// what lets the whole render layer stay untouched while the wire format
// changes: only this file and `client.ts` know both shapes.

import type { FireWall, FireDoor } from "./derive";
import type { ApartmentLevel, ApartmentFlat, ApartmentRoom, ApartmentDoor }
  from "./plan/ApartmentView";
import type { SiteDoor, SiteLayer } from "./client";
import { manaDinProps, type ManaCurenta } from "./manaCurenta";

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

export interface Source {
  kind: "dwg" | "ifc" | "solver" | "manual";
  /** WHICH generator, when several write one document. `kind` says how the data
   *  was obtained and both stages of a pipeline are "solver", so it cannot
   *  answer "who decided this" — which is what tells an upstream object from a
   *  later stage's re-derivation of the same thing. */
  engine?: string | null;
  file_id?: string | null;
  layer?: string | null;
  confidence?: number | null;
}

/** Every entity carries this envelope. `display` is everything about the
 *  drawing rather than the building; `props` is the namespaced extension point
 *  for supplier data, finisaje and mobilă. */
/** Every object carries this envelope.
 *
 *  `type` is the class — Wall, Door, Space — and `properties.kind` the subtype
 *  within it. `properties` is what you index and join on; `geometry` is where
 *  the thing is; `display` is drawing only and may be ignored entirely. */
export interface CanonicalObject {
  id: string;
  type: ObjectType;
  properties: ObjectProperties;
  geometry?: Record<string, any>;
  props?: Record<string, unknown>;
  /** Absent when it matches `doc.defaults.source` — see `sourceOf`. */
  source?: Source;
  display?: Record<string, any>;
}

// From the generated catalog, so the browser cannot believe in a type the
// emitter does not produce. See `kinds.TYPE`.
export type { ObjectType } from "./generated/kinds";
import type { ObjectType } from "./generated/kinds";
// Etichetele românești ale felurilor noi de zonă (3.18) vin din catalogul
// generat, nu din i18n: sunt numele formatului, aceleași în toate limbile.
import { LABEL } from "./generated/kinds";
import { areArce, teseleazaBulges } from "./plan/bulges";
import type { Pardoseala } from "./plan/pardoseala3d";
import type { CortinaPanou } from "./plan/cortina3d";
// Numele românești ale kind-urilor, din catalogul generat: eticheta unui spațiu
// importat fără `display.label` se citește de acolo, nu dintr-o a doua listă.
import { numeleFelului } from "./vocabular";
import { t } from "../i18n";
import type { Cheie } from "../i18n";

export interface ObjectProperties {
  kind?: string | null;
  /** The physical floor. Every object has exactly one. */
  level?: number | null;
  storey?: string | null;
  /** Links, all ids. */
  building?: string | null;
  bar?: string | null;
  core?: string | null;
  flat?: string | null;
  wall?: string | null;
  room?: string | null;
  site?: string | null;
  /** The floor-free identity of something standing on several levels — a
   *  column through the slab, a shaft, the same flat one floor up. */
  group?: string | null;
  [k: string]: any;
}

export interface CanonicalDoc {
  schema_version: string;
  /** Present on stream frames only; a stored project carries it too, because
   *  the browser saves the `done` frame as it arrived. */
  kind?: "incumbent" | "apartments" | "done" | "cancelled";
  units: { length: "m"; angle: "deg"; area: "m2" };
  crs: { axis: string; origin: number[]; epsg?: number | null;
         true_north_deg?: number | null };
  project?: { id?: string | null; name?: string | null;
              generated_at?: string; engine?: string | null } | null;
  defaults?: { source?: Source };
  lot?: { id?: string; boundary?: number[][]; entry?: any; blockers?: any[];
          brief?: Record<string, any> } | null;
  /** ONE flat list. No storey tree, no per-storey buckets. */
  objects: CanonicalObject[];
  compliance?: any;
  diagnostics?: Record<string, any> | null;
}

export const SCHEMA_MAJOR = 3;

/** True for a canonical document THIS reader can read.
 *
 *  The major is checked, and it was not before: the constant above was declared
 *  and never read, so a document from a future major would have been decoded
 *  field by field against a shape that had moved, and drawn wrong rather than
 *  refused. Python refuses it loudly in `canonical_model.validate`; this is the
 *  same rule on this side of the wire.
 *
 *  A minor is deliberately NOT checked — a minor bump only adds optional fields,
 *  so an older reader renders a newer document minus what it does not know
 *  about, which is the whole reason for promising that. */
/** The geometry keys that are POSITIONS, so they move with the frame.
 *
 *  ONE list, read by everything that re-frames a document — `northUp` turns
 *  these keys and `laOrigineLocala` translates them — so a key cannot be
 *  handled by one and forgotten by the other.
 *
 *  Enumerated from the real documents, not guessed. Their neighbours — `w`,
 *  `h`, `width_m`, `thickness_m`, `deg`, `u_m`, `slope` … — are MAGNITUDES,
 *  and a moved magnitude is a broken one. That is why the list is a whitelist
 *  (what moves) and not a blacklist (what does not): a key newly added to the
 *  format is left alone, which is the harmless half of the mistake. */
// `parts` (3.4): plăcile unui planșeu în mai multe bucăți — inele de inele,
// ca `holes`, deci trec prin aceleași transformări.
const POSITION_KEYS = ["poly", "holes", "axis", "mouth", "parts"];

/** Puncte de LUME ținute în `display`, ca listă de chei.
 *
 *  `display` e locul unde stau formele care sunt DESEN, nu model — apronul unei
 *  rampe, palierul ei, conturul inelului unei rampe elicoidale, platelajul unui
 *  planșeu. Sunt totuși puncte de lume, iar `northUp` le sărea: pe o grilă
 *  northing-first restul planșei se întorcea un sfert de tură, iar ele rămâneau
 *  în coordonatele brute — adică la sute de kilometri de desenul lor, deci
 *  invizibile. Așa n-a apărut niciodată inelul unei rampe elicoidale pe planșa
 *  de parcaj (Oana, 20.09.2026).
 *
 *  Lista e închisă, ca `POSITION_KEYS`: `display` e deschis pentru orice, iar o
 *  transformare oarbă ar roti și ce nu e punct. */
const DISPLAY_WORLD_POINTS = ["poly", "apron", "mouth", "axis", "axis_path", "keep_1", "keep_2",
                              "top", "bottom", "center", "deck", "road", "label_xy"];

/** Puncte în coordonate de LUME ținute în `props`, ca „cheie -> câmpuri".
 *
 *  `laOrigineLocala` rescrie numai `geometry`, iar `props` e punctul de
 *  extensie deschis — deci o coordonată pusă acolo scapă deplasării și rămâne
 *  la sute de kilometri de desenul ei. Exact defectul pe care `adnotareLaOrigine`
 *  îl repară pentru adnotările validatorului; ăsta e același, pentru props.
 *
 *  Măsurat pe `architecture.evac_critical_point`: scara desenată la (665, 741)
 *  în cadrul local, punctul care a cerut-o raportat la (324236, 585689) în
 *  Stereo 70, și o linie punctată care ieșea din teren.
 *
 *  Lista e mică pe purpose: o coordonată are locul ei în `geometry`, iar una
 *  ținută în `props` e o excepție care trebuie enumerată aici ca să se miște cu
 *  planul. */
const PROPS_WORLD_POINTS: Record<string, string[]> = {
  "architecture.evac_critical_point": ["point", "path_new", "path_old"],
  // Mobila și aparatele unui nivel, scrise de editorul de nivel pe `Storey`
  // (v. `plan/nivelTronson.ts::AMENAJARE`): centrele lor sunt puncte de lume.
  "architecture.amenajare": ["piese_c", "aparate_c"],
  // Obiectele sanitare ale grupului sanitar al unui adăpost ALA (`ala.pas`):
  // centrele lor, ca la amenajare.
  "architecture.ala": ["obiecte_c"],
  // Bucățile unui corp cu înălțimi diferite (`layout.trepte`), ca liste de
  // inele; și liniile rosturilor. Scrise de arhitectură și de planul de situație.
  "architecture.trepte": ["contururi", "linii"],
  "site_plan.trepte": ["contururi"],
  // Casa de scară de evacuare a parcajului (formatul 3.16, §35): cutia cu pereți și
  // rampele, ca perechi de puncte
  "architecture.evacuare": ["contur", "rampe"],
};

/** DIRECȚII în coordonate de lume ținute în `props`: se ÎNTORC cu cadrul (un
 *  sfert de tură sau oglindirea lui `northUp`), dar NU se deplasează cu el —
 *  un vector mutat la origine ar deveni un punct oarecare.
 *
 *  Măsurat pe `architecture.facade.normal` (18.09.2026): axa feței trecea prin
 *  `northUp`, normala ei nu. Pe ramura oglindită normala fețelor lungi rămânea
 *  cu semnul lui y neschimbat, adică arăta ÎN clădire: pielea de fațadă se
 *  construia spre interior, la planul zidăriei, cărămida și plăcarea se băteau
 *  pe muchia comună, iar la colț cornișa se mătura în corp și cea de pe
 *  fronton se prelungea în aer. */
const PROPS_WORLD_DIRECTIONS: Record<string, string[]> = {
  "architecture.facade": ["normal"],
  // …iar orientarea lor e o DIRECȚIE, nu un unghi: un unghi nu trece prin
  // oglindire fără să știi în jurul cărei axe, un versor trece singur.
  "architecture.amenajare": ["piese_dir", "aparate_dir"],
  // …spatele fiecărui obiect sanitar ALA, ca direcție, și sensul ieșirii unei
  // sărituri de lup (`spre`): pe un sit rotit cu 4°, netrecut prin oglindire,
  // ieșea cu 8° față de laturile puțului (28.09.2026)
  "architecture.ala": ["obiecte_dir", "spre"],
};

/** `props`, cu punctele din `PROPS_WORLD_POINTS` trecute prin `fn`.
 *
 *  O singură traversare, folosită de AMÂNDOUĂ transformările de cadru —
 *  `northUp` (sfertul de tură) și `laOrigineLocala` (deplasarea) — fiindcă
 *  invariantul scris la `northUp` cere ca o cheie să nu poată fi mutată de una
 *  și uitată de cealaltă. Cu `geometry` asta e asigurat de `POSITION_KEYS`;
 *  pentru `props`, de aici.
 *
 *  Întoarce ACELAȘI obiect când nu e nimic de făcut, ca apelantul să poată sări
 *  copierea: majoritatea obiectelor n-au `props` deloc. */
/** `fn` aplicat pe orice punct dintr-o valoare: un punct, o polilinie, sau
 *  liste de liste.
 *
 *  Recursiv, ca `deplaseaza` de mai jos, și pentru același motiv: câmpurile din
 *  `PROPS_WORLD_POINTS` nu sunt toate puncte simple — `point` e un punct, iar
 *  `path_new`/`path_old` sunt trasee mers, adică șiruri de puncte. O regulă care
 *  ar trebui să știe dinainte care e care ar fi un al doilea loc de ținut în
 *  sincron cu emitentul.
 *
 *  Întoarce ACEEAȘI valoare când nu e nimic de mutat, ca apelantul să poată
 *  sări copierea. */
function puncteRecursiv(v: unknown, fn: (p: number[]) => number[]): unknown {
  if (!Array.isArray(v) || v.length === 0) return v;
  if (typeof v[0] === "number") {
    return v.length >= 2 && typeof v[1] === "number" ? fn(v as number[]) : v;
  }
  let schimbat = false;
  const out = v.map((x) => {
    const y = puncteRecursiv(x, fn);
    if (y !== x) schimbat = true;
    return y;
  });
  return schimbat ? out : v;
}

/** `display` cu punctele de lume duse în cadrul desenului.
 *
 *  O singură traversare, folosită de AMÂNDOUĂ transformările de cadru —
 *  `northUp` și `laOrigineLocala` — din același motiv ca `propsPuncte`: o cheie
 *  mutată de una și uitată de cealaltă ajunge tot în afara desenului. Prima
 *  oară am pus-o doar în `northUp`, iar conturul rampei a rămas la 583 000 în
 *  timp ce planul s-a mutat la origine (Oana, 20.09.2026).
 *
 *  `oglindit`: desenul e răsturnat, nu rotit, deci un arc parcurs într-un sens
 *  ajunge parcurs invers — `sweep_deg`, singura mărime de aici care poartă un
 *  sens de rotație, își schimbă semnul. O deplasare nu oglindește nimic.
 *  Unghiurile simple (`deg`) stau în `geometry` și le întoarce `geo`. */
function afisajPuncte(
  display: Record<string, unknown> | undefined,
  fn: (p: number[]) => number[],
  oglindit = false,
): Record<string, unknown> | undefined {
  if (!display) return display;
  let out: Record<string, unknown> | undefined;
  for (const c of DISPLAY_WORLD_POINTS) {
    const mutat = puncteRecursiv(display[c], fn);
    if (mutat === display[c]) continue;
    out = out ?? { ...display };
    out[c] = mutat;
  }
  if (oglindit && typeof display.sweep_deg === "number") {
    out = out ?? { ...display };
    out.sweep_deg = -display.sweep_deg;
  }
  return out ?? display;
}

const CHEI_PUNCTE = Object.entries(PROPS_WORLD_POINTS);
const CHEI_DIRECTII = Object.entries(PROPS_WORLD_DIRECTIONS);

function propsPuncte(
  props: Record<string, unknown> | undefined,
  fnPuncte: (p: number[]) => number[],
  directii?: (p: number[]) => number[],
): Record<string, unknown> | undefined {
  if (!props) return props;
  let out: Record<string, unknown> | undefined;
  // Cheile se iau din listele de la nivelul modulului, nu se refac per obiect:
  // funcția trece de două ori prin fiecare obiect al documentului.
  for (let i = 0; i < CHEI_PUNCTE.length + (directii ? CHEI_DIRECTII.length : 0); i++) {
    const [cheie, campuri] = i < CHEI_PUNCTE.length ? CHEI_PUNCTE[i] : CHEI_DIRECTII[i - CHEI_PUNCTE.length];
    const fn = i < CHEI_PUNCTE.length ? fnPuncte : directii!;
    // Din ce s-a mutat deja, nu din original: o cheie cu și puncte, și direcții
    // (`architecture.amenajare`) trece de două ori, iar a doua trecere, citită
    // din original, ar fi pus la loc punctele nemutate peste cele mutate.
    const val = (out ?? props)[cheie];
    if (!val || typeof val !== "object") continue;
    const src = val as Record<string, unknown>;
    let nou: Record<string, unknown> | undefined;
    for (const c of campuri) {
      const mutat = puncteRecursiv(src[c], fn);
      if (mutat === src[c]) continue;
      nou = nou ?? { ...src };
      nou[c] = mutat;
    }
    if (nou) {
      out = out ?? { ...props };
      out[cheie] = nou;
    }
  }
  return out ?? props;
}

/** The grids whose coordinate pair is (northing, easting) rather than the
 *  ordinary GIS (easting, northing). The twin of
 *  `canonical_model.read.NORTHING_FIRST_EPSG`; a test keeps the two together. */
export const NORTHING_FIRST_EPSG = [3844];

/** Is this document's pair (northing, easting)?
 *
 *  `crs.axis` says `x_east_y_north_z_up` on every document, and for most
 *  producers that is the truth. It is NOT the truth on EPSG 3844 (Stereo 70),
 *  which the EPSG registry itself defines northing-first: axis 1 is Northing,
 *  abbreviated `X`. The axis string is not a claim any producer made about
 *  these documents — it is an un-overridden default merged into every one of
 *  them — so the CODE is believed and the string is not.
 *
 *  Measured, not argued: `[326208.314, 583087.869]` read northing-first is
 *  44.43 N / 26.04 E, Militari in Bucharest; read the other way it is
 *  Budureasa in Bihor, 363 km away. */
export const northingFirst = (crs?: { epsg?: number } | null): boolean =>
  NORTHING_FIRST_EPSG.includes(crs?.epsg as number);

/** Is the document tied to a real-world grid, so that "north" means something?
 *
 *  `epsg: null` is a producer that placed the building in a frame of its own
 *  making — a plan measured from its own corner — and there is no north on it
 *  to point at. Every georeferenced document is north-up after `northUp`,
 *  whatever its grid, which is why this and not the grid is what a compass
 *  should be asked about. */
export const isGeoreferenced = (crs?: { epsg?: number | null } | null): boolean =>
  crs?.epsg != null;

/** The document in the DRAWING frame: east to the right, north UP on screen.
 *
 * WHY THE DOCUMENT AND NOT THE TRANSFORM. The document is a GEOGRAPHIC model
 * — north is a compass direction, not a screen direction — while SVG draws y
 * DOWNWARD and the viewer's transform is `translate(tx ty) scale(k)` and
 * nothing else. A `scale(k, -k)` on the top group would mirror the TEXT too:
 * thirty-eight labels upside down, each needing its own counter-mirror on top
 * of the rotations some already carry. Turned once here, at the mouth,
 * everything downstream — this module's translations, the rendering, mouse
 * targeting, `fitBbox`, the validator's annotations, the 3D scene and the PDF
 * export — works in ONE space, and the text stays upright untouched.
 *
 * WHICH CORRECTION, and why it is a choice of one. There are two, and they are
 * mutually exclusive premises rather than two versions of one rule:
 *
 *   * on a northing-first grid the pair is (north, east), so the map is the
 *     QUARTER TURN `(a, b) -> (b, -a)` — east goes right, north goes up. It
 *     preserves handedness, so nothing downstream needs a sign changed, only a
 *     bearing: `deg - 90`. This is `canonical_model.read.north_up_xy`.
 *   * otherwise the pair is (east, north) exactly as `crs.axis` declares, and
 *     all that is owed is the screen's y flip, `(x, y) -> (x, -y)`, which
 *     reverses the sense of angles: `-deg`.
 *
 * Applied to a document that is already northing-first, the y flip is simply
 * wrong — on 27.08.2026 two branches wrote one each and a merged drawing got
 * BOTH. So the CRS is asked once, here, and never again: no layer, screen or
 * discipline downstream may branch on it. That is the invariant — a rule
 * applied to the site layer but not to the storeys is exactly how the
 * architecture of a project came out a quarter turn from its own site plan.
 *
 * WHAT TURNS, exactly. The position keys are `POSITION_KEYS` — the same whitelist
 * `laOrigineLocala` translates, so a key cannot be moved by one and forgotten
 * by the other — plus `cx`/`cy`, which are mapped as a PAIR (a quarter turn
 * mixes them; negating `cy` alone would only ever be right for a mirror), and
 * the stored bearings `deg` and `grid_deg`.
 *
 * `phi_deg` is NOT touched: it is the angle of internal friction from the
 * geotechnical report, a degree with nothing to do with orientation. Nor are
 * `elevation_m`, `height_m`, `thickness_m`, `width_m`, `u_m`, `w`, `h`,
 * `overhang_m` — those are MAGNITUDES, and a turned magnitude is a broken one.
 *
 * WHAT REMAINS, and it shows: a glyph placed with `rotate(deg)` is MOVED
 * correctly, but on the mirror branch its own drawing is reflected, not
 * rotated. For symmetric glyphs (stalls, columns, windows) that is the same
 * thing; for handed ones — a door's swing arc, a stair's UP arrow — the hand
 * comes out wrong. The quarter-turn branch has no such debt, being a rotation.
 * The building's geometry (walls, slabs, spaces, the outline) is right in full
 * either way, because polygons map exactly. */
export function northUp<T>(doc: T): T {
  return orienteaza(doc, false);
}

/** DRUMUL ÎNAPOI al lui `northUp`: din cadrul desenului în cel al documentului.
 *
 *  Îl cere editorul de nivel (`plan/nivelTronson.ts`), care scrie în soluție
 *  obiecte mutate pe ecran. Oglindirea e propria ei inversă; sfertul de tură
 *  se desface cu sfertul de tură în sens opus. Tot restul — ce chei sunt
 *  puncte, ce chei sunt direcții, `sweep_deg` — e ACELAȘI cod ca la venire,
 *  fiindcă o cheie întoarsă la dus și uitată la întors ar scrie în soluție un
 *  punct rotit un sfert de tură. */
export function northDown<T>(doc: T): T {
  return orienteaza(doc, true);
}

function orienteaza<T>(doc: T, invers: boolean): T {
  if (!doc || typeof doc !== "object") return doc;
  const d = doc as any;
  const turn = northingFirst(d.crs);
  const pt = turn
    ? (invers ? (p: number[]) => [-p[1], p[0], ...p.slice(2)]
              : (p: number[]) => [p[1], -p[0], ...p.slice(2)])
    : (p: number[]) => [p[0], -p[1], ...p.slice(2)];
  const bearing = turn ? (invers ? (a: number) => a + 90 : (a: number) => a - 90)
                       : (a: number) => -a;
  const points = (v: any): any =>
    Array.isArray(v) && Array.isArray(v[0])
      ? (Array.isArray(v[0][0])
          ? v.map(points)                                  // rings of rings
          : v.map((p: number[]) => pt(p)))
      : v;
  const geo = (g: any) => {
    if (!g || typeof g !== "object") return g;
    const out: any = { ...g };
    for (const k of POSITION_KEYS) if (out[k] != null) out[k] = points(out[k]);
    // A centre is one POINT split across two keys, so it maps as a point. The
    // format never writes one without the other; if it ever did, leaving it
    // alone is the harmless half of the mistake.
    if (typeof out.cx === "number" && typeof out.cy === "number") {
      [out.cx, out.cy] = pt([out.cx, out.cy]);
    }
    for (const k of ["deg", "grid_deg"]) {
      if (typeof out[k] === "number") out[k] = bearing(out[k]);
    }
    // `bulge` e sensul în care crește unghiul de la axis[0] la axis[1]: sfertul
    // de tură îl păstrează, oglindirea îl întoarce — ca pe un unghi
    if (typeof out.bulge === "number" && !turn) out.bulge = -out.bulge;
    // `bulges` (3.17, §36), câte unul pe muchie, se întorc la fel: o reflexie
    // schimbă sensul fiecărui arc, sfertul de tură nu
    if (Array.isArray(out.bulges) && !turn) {
      out.bulges = out.bulges.map((b: unknown) => (typeof b === "number" ? -b : b));
    }
    return out;
  };
  return {
    ...d,
    objects: Array.isArray(d.objects)
      ? d.objects.map((o: any) => {
          if (!o) return o;
          // `props` trece prin ACEEAȘI hartă de puncte ca geometria: un punct
          // de lume ținut acolo, netrecut, ar rămâne rotit un sfert de tură
          // față de desenul lui.
          // `pt` e liniară (fără deplasare), deci întoarce la fel un punct și
          // o direcție; direcțiile trec DOAR pe aici, nu și prin `laOrigineLocala`.
          const props = propsPuncte(o.props, pt, pt);
          const geometry = o.geometry ? geo(o.geometry) : o.geometry;
          const display = afisajPuncte(o.display, pt, !turn);
          if (props === o.props && geometry === o.geometry && display === o.display) return o;
          // Fiecare jumătate se pune numai dacă s-a schimbat: altfel un obiect
          // fără `props` ar primi cheia cu `undefined`, iar forma obiectului nu
          // se schimbă degeaba.
          return {
            ...o,
            ...(geometry !== o.geometry ? { geometry } : {}),
            ...(props !== o.props ? { props } : {}),
            ...(display !== o.display ? { display } : {}),
          };
        })
      : d.objects,
    // A site-plan document carries geometry on the envelope's lot too.
    ...(d.lot && typeof d.lot === "object" ? { lot: geo(d.lot) } : {}),
  } as T;
}

export function isCanonical(x: any): x is CanonicalDoc {
  if (!x || typeof x !== "object" || !Array.isArray(x.objects)) return false;
  if (typeof x.schema_version !== "string") return false;
  return Number(x.schema_version.split(".")[0]) === SCHEMA_MAJOR;
}

/** Provenance for one object, resolved against the document default. */
export function sourceOf(o: CanonicalObject, doc: CanonicalDoc): Source {
  return o.source ?? doc.defaults?.source ?? { kind: "solver", confidence: 1 };
}

// ---------------------------------------------------------------------------
// Indexing — the whole reason the list is flat
// ---------------------------------------------------------------------------

/** Group the list by any property. `indexBy(doc)` -> by type;
 *  `indexBy(doc, "level")` -> by floor. One pass, any grouping. */
export function indexBy(doc: CanonicalDoc, key = "type"): Map<any, CanonicalObject[]> {
  const out = new Map<any, CanonicalObject[]>();
  for (const o of doc.objects ?? []) {
    const k = key === "type" || key === "id" ? (o as any)[key] : o.properties?.[key];
    const list = out.get(k);
    if (list) list.push(o); else out.set(k, [o]);
  }
  return out;
}

export function byId(doc: CanonicalDoc): Map<string, CanonicalObject> {
  return new Map((doc.objects ?? []).map((o) => [o.id, o]));
}

/** INDEXUL DOCUMENTULUI, construit o dată, la prima întrebare.
 *
 *  Traducerile de mai jos întrebau, per planșeu, „ce stă pe el" — și fiecare
 *  întrebare era o parcurgere a TUTUROR obiectelor. Pe un document de 68 000
 *  de obiecte cu 73 de planșee, `toStoreyPlans`, `toPlans`, `toSiteLayer` și
 *  etajele de apartamente ajungeau la zeci de milioane de comparații, adică
 *  secunde pe firul principal la deschiderea documentului.
 *
 *  Grupările țin ORDINEA DOCUMENTULUI, exact ca filtrele pe care le înlocuiesc,
 *  iar cheile sunt aceleași valori comparate (`o.type`, `o.properties?.storey`,
 *  `o.id`), deci răspunsurile sunt aceleași, la obiect.
 *
 *  Cheia e lista `objects`, nu documentul: un document refăcut cu `{...doc}`
 *  își păstrează lista și deci indexul, iar unul cu obiectele schimbate are
 *  altă listă. Documentul e tratat ca imuabil peste tot în arbore; lungimea
 *  listei e o gardă ieftină pentru cineva care l-ar crește pe loc. */
interface IndexDocument {
  lungime: number;
  /** `type` → obiecte, în ordinea documentului. */
  peTip: Map<unknown, CanonicalObject[]>;
  /** `properties.storey` → `type` → obiecte, fără `Storey` — v. `onStorey`. */
  pePlanseu: Map<unknown, Map<string, CanonicalObject[]>>;
  /** `id` → PRIMUL obiect cu id-ul ăsta, ca `objects.find((o) => o.id === id)`. */
  primulCuId: Map<unknown, CanonicalObject>;
  /** `storeysOf(doc)` fără proprietar, calculat la cerere. */
  planseeSortate?: CanonicalObject[];
}
const INDEXURI = new WeakMap<object, IndexDocument>();
const FARA_OBIECTE: CanonicalObject[] = [];

function indexul(doc: CanonicalDoc): IndexDocument {
  const lista = (doc.objects ?? FARA_OBIECTE) as CanonicalObject[];
  const cacheabil = Array.isArray(lista);
  const vechi = cacheabil ? INDEXURI.get(lista) : undefined;
  if (vechi && vechi.lungime === lista.length) return vechi;
  const peTip = new Map<unknown, CanonicalObject[]>();
  const pePlanseu = new Map<unknown, Map<string, CanonicalObject[]>>();
  const primulCuId = new Map<unknown, CanonicalObject>();
  for (const o of lista) {
    const t = o.type;
    const l = peTip.get(t);
    if (l) l.push(o); else peTip.set(t, [o]);
    if (!primulCuId.has(o.id)) primulCuId.set(o.id, o);
    if (t === "Storey") continue;
    const s = o.properties?.storey;
    let m = pePlanseu.get(s);
    if (!m) { m = new Map(); pePlanseu.set(s, m); }
    const ls = m.get(t);
    if (ls) ls.push(o); else m.set(t, [o]);
  }
  const idx: IndexDocument = { lungime: cacheabil ? lista.length : -1, peTip, pePlanseu, primulCuId };
  if (cacheabil) INDEXURI.set(lista, idx);
  return idx;
}

/** Obiectele unui tip, în ordinea documentului — `objects.filter((o) => o.type === t)`.
 *  Lista e a indexului: se citește, nu se modifică. */
function deTip(doc: CanonicalDoc, t: ObjectType): readonly CanonicalObject[] {
  return indexul(doc).peTip.get(t) ?? FARA_OBIECTE;
}

/** `storeysOf(doc)` fără proprietar, din index: lista e a indexului, deci se
 *  citește, nu se modifică. */
function planseeSortate(doc: CanonicalDoc): readonly CanonicalObject[] {
  const idx = indexul(doc);
  return idx.planseeSortate ??= sorteazaPlansee(
    (idx.peTip.get("Storey") ?? FARA_OBIECTE).slice());
}

const sorteazaPlansee = (l: CanonicalObject[]): CanonicalObject[] =>
  l.sort((a, b) => ((a.properties.level ?? 0) - (b.properties.level ?? 0))
                   || (ownerOf(a) ?? "").localeCompare(ownerOf(b) ?? ""));

/** The structure a storey is a floor of — a Building id or a basement Area id. */
export function ownerOf(st: CanonicalObject): string | undefined {
  return st.properties.building ?? st.properties.area ?? undefined;
}

/** Ușile de intrare în bloc de la parter, cu sensul spre exterior.
 *
 *  Ușa vine din arhitectură cu centrul, direcția foii închise (`deg`, de-a lungul
 *  peretelui) și lățimea; pe care parte e curtea o spune amprenta corpului: se
 *  probează un punct la o jumătate de metru de fiecare parte a peretelui, iar
 *  cel din afara tuturor amprentelor dă normala exterioară. Fără ea săgeata de
 *  intrare ar putea arăta în bloc sau afară la întâmplare. */
function usileBlocului(doc: CanonicalDoc, bars: { pts?: number[][] }[]): SiteDoor[] {
  const amprente = bars.map((b) => b.pts as number[][]).filter((p) => (p?.length ?? 0) >= 3);
  const inAmprenta = (x: number, y: number) => amprente.some((inel) => punctInInel(inel, x, y));
  const out: SiteDoor[] = [];
  for (const o of deTip(doc, "Door")) {
    if (o.properties.role !== "entry") continue;
    const cx = g(o, "cx") as number, cy = g(o, "cy") as number;
    const deg = g(o, "deg") as number, w = (g(o, "width_m") as number) ?? 1.8;
    if (![cx, cy, deg].every(Number.isFinite)) continue;
    const a = (deg * Math.PI) / 180;
    const px = -Math.sin(a), py = Math.cos(a);       // perpendicular pe foaie
    const afara: [number, number] = inAmprenta(cx + px * 0.5, cy + py * 0.5) ? [-px, -py] : [px, py];
    out.push({ eid: o.id, cx, cy, deg, w, n: afara });
  }
  return out;
}

/** Balcoanele care IES din amprenta corpurilor, o dată per poziție — de la
 *  parter (`parter`) sau de la etaje.
 *
 *  «Iese» = are cel puțin un colț în afara tuturor barelor de la sol. E un
 *  test grosier — un balcon care iese fără să-și scoată vreun colț n-ar fi
 *  prins — dar în arbore nu există bibliotecă de operații booleene pe
 *  poligoane, iar un balcon se avântă întotdeauna cu colțuri cu tot.
 *
 *  ACELAȘI balcon se repetă pe fiecare etaj: cincisprezece etaje înseamnă
 *  cincisprezece poligoane identice, care desenate unul peste altul dau o
 *  muchie neagră din simpla lor suprapunere. Se ține unul singur per poziție. */
function balcoaneCareIes(doc: CanonicalDoc, bars: { pts?: number[][] }[], parter: boolean): number[][][] {
  const amprente = bars.map((b) => b.pts as number[][])
    .filter((p) => (p?.length ?? 0) >= 3);
  if (!amprente.length) return [];
  const inAmprenta = (x: number, y: number) =>
    amprente.some((inel) => punctInInel(inel, x, y));
  const nivel = new Map(planseeSortate(doc).map((st) => [st.id, st.properties.level as number | undefined]));
  const pePozitie = new Map<string, number[][]>();
  for (const o of deTip(doc, "Space")) {
    if (o.properties.kind !== "balcony") continue;
    if ((nivel.get(o.properties.storey as string) === 0) !== parter) continue;
    const poly = inel(o);
    if (poly.length < 3) continue;
    if (!poly.some(([x, y]) => !inAmprenta(x, y))) continue;
    const cx = poly.reduce((t, q) => t + q[0], 0) / poly.length;
    const cy = poly.reduce((t, q) => t + q[1], 0) / poly.length;
    const cheie = `${Math.round(cx * 10)}:${Math.round(cy * 10)}`;
    if (!pePozitie.has(cheie)) pePozitie.set(cheie, poly);
  }
  return [...pePozitie.values()];
}

/** Every Storey object, ordered garage-upward — optionally one structure's.
 *
 *  A storey is a floor of ONE structure, so there are as many storeys at level
 *  -1 as there are basements. Ordered by level and then by owner, so the order
 *  is total and does not depend on the emission order. */
export function storeysOf(doc: CanonicalDoc, owner?: string): CanonicalObject[] {
  // O copie: cine primește lista o poate sorta sau tăia fără să atingă indexul.
  if (owner === undefined) return planseeSortate(doc).slice();
  return sorteazaPlansee(deTip(doc, "Storey").filter((o) => ownerOf(o) === owner));
}

/** Everything standing on ONE floor of ONE structure, by storey id.
 *
 *  Takes a storey id, not a level number. A level number identified a floor only
 *  while there was one storey per level — which is exactly what made four
 *  separate basements read as a single parking. */
export function objectsOn(doc: CanonicalDoc, storeyId: string): CanonicalObject[] {
  return (doc.objects ?? [])
    .filter((o) => o.id === storeyId || o.properties?.storey === storeyId);
}

/** Every level number in the document, deepest first — one per height, however
 *  many structures reach it. */
export function levelsOf(doc: CanonicalDoc): number[] {
  const out = new Set<number>();
  for (const st of storeysOf(doc)) {
    if (st.properties.level != null) out.add(st.properties.level);
  }
  return [...out].sort((a, b) => a - b);
}

// ---------------------------------------------------------------------------
// Document -> view models
// ---------------------------------------------------------------------------
//
// The renderer keeps its own shapes (`SolvedPlan`, `ApartmentLevel`) because it
// wants rooms back inside their flat and a bay's box in metres. The document is
// flat and normalised; this is the one place that knows both.

import type { SolvedPlan, SolvedSpace, RoadCell, SolverRoom, SolverColumn }
  from "./client";

/** Objects on one floor, grouped by type.
 *
 *  Keyed on the STOREY, not on a level number: `level` was a copy of
 *  `storey.level` carried on every object, and it is gone — a bare level number
 *  now names a set of floors, one per structure. */
function onStorey(doc: CanonicalDoc, storeyId: string): ReadonlyMap<string, readonly CanonicalObject[]> {
  // Din index: aceleași grupe, în aceeași ordine, ca parcurgerea de dinainte
  // (`o.properties?.storey === storeyId`, fără `Storey`). Harta e a indexului:
  // se citește, nu se modifică.
  return indexul(doc).pePlanseu.get(storeyId) ?? new Map();
}

/** Objects at one height, ACROSS every structure — the site-wide question a
 *  section drawing and a green-space balance both genuinely ask. */
function atLevel(doc: CanonicalDoc, level: number): Map<string, CanonicalObject[]> {
  const floors = new Set(storeysOf(doc)
    .filter((st) => st.properties.level === level).map((st) => st.id));
  const out = new Map<string, CanonicalObject[]>();
  for (const o of doc.objects ?? []) {
    if (o.type === "Storey" || !floors.has(o.properties?.storey ?? "")) continue;
    const list = out.get(o.type);
    if (list) list.push(o); else out.set(o.type, [o]);
  }
  return out;
}

const g = (o: CanonicalObject, k: string) => o.geometry?.[k];

/** One object as a drawable surface: its ring, and its holes when it has any.
 *
 *  `holes` is not optional detail here. It is the difference between a pavement
 *  band and a filled block, and on one real scheme between 6,257 m² of pavement
 *  and 23,383. */
/** Id-ul agentului planului de situație pe un obiect («N3», «T12», «R7.4»),
 *  din `props["site_plan.agent"].id` (3.18, §37); gol când nu e al lui. Ca
 *  fragment de întins, deci nu pune cheia pe obiectele fără id. */
const idAgent = (o: CanonicalObject): { agent?: string } => {
  const a = ((o.props ?? {}) as Record<string, unknown>)["site_plan.agent"] as { id?: unknown } | undefined;
  return typeof a?.id === "string" && a.id ? { agent: a.id } : {};
};

const surface = (o: CanonicalObject) => {
  const holes = g(o, "holes") as number[][][] | undefined;
  const poly = g(o, "poly") as number[][];
  return (holes && holes.length) ? { poly, holes } : poly;
};

/** Area of a ring, minus its holes. Shoelace — the document carries rings, and
 *  several of the shapes it carries are annular (a pavement band round a
 *  building, a green ring grown in from the property line), so the holes are not
 *  optional detail: ignoring them on one sample over-claimed green-on-soil by
 *  3.4x, against the figure the 20%-of-plot quota is checked on. */
/** Cade punctul în inel? Aruncare de rază, algoritmul obișnuit. */
function punctInInel(inel: number[][], x: number, y: number): boolean {
  let inauntru = false;
  for (let i = 0, j = inel.length - 1; i < inel.length; j = i++) {
    const [xi, yi] = inel[i];
    const [xj, yj] = inel[j];
    if ((yi > y) !== (yj > y)
        && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inauntru = !inauntru;
  }
  return inauntru;
}

function ringArea(ring?: number[][]): number {
  if (!ring || ring.length < 3) return 0;
  let a = 0;
  for (let i = 0, n = ring.length; i < n; i++) {
    const [x0, y0] = ring[i], [x1, y1] = ring[(i + 1) % n];
    a += x0 * y1 - x1 * y0;
  }
  return Math.abs(a) / 2;
}

function shapeArea(o: CanonicalObject): number {
  const holes: number[][][] = g(o, "holes") ?? [];
  return ringArea(g(o, "poly")) - holes.reduce((t, h) => t + ringArea(h), 0);
}

/** What counts as a CAMERĂ. `Flat.rooms` used to carry this number, and it did
 *  not agree with `room_ids`: three against nine on the same dwelling, because
 *  one meant habitable rooms and the other every space. Counting from the kinds
 *  cannot disagree with the rooms that are there. */
const HABITABLE = new Set(["living", "bedroom", "studio"]);

/** Every parking FLOOR OF EVERY BASEMENT as its own drawable plan.
 *
 *  One per (structure, level), which is what makes four separate garages four
 *  drawings. They used to collapse onto one plan per level, so a scheme's four
 *  parkings showed as a single "305 locuri" that no bay could be attributed to
 *  and no per-parking fact could hang off. `plan.label` names which one. */
export function toPlans(doc: CanonicalDoc): SolvedPlan[] {
  return storeysOf(doc).filter((st) => eParcare(doc, st))
    .map((st) => toPlan(doc, st));
}

/** E parcaj nivelul ăsta? Se întreabă CE STĂ pe el, nu cum l-a etichetat cine
 *  l-a emis.
 *
 *  Filtrul citea `storey_kind`, și lua parcările ȘI tot parterul — bun câtă
 *  vreme solverul era singurul producător (parterul lui importat avea 550 de
 *  locuri la sol), greșit din clipa în care a emis importul DWG un parter de
 *  locuință: fără niciun loc de parcare, ajungea tot la desenul de parcaj, se
 *  numea „Parcare · nivel 0" și se desena ca o placă goală cu stâlpi pe ea.
 *  Un nivel cu locuri, cu alei sau cu platelaj e parcaj; unul cu pereți și
 *  camere e un plan de arhitectură și are acum desenul lui (`StoreyView`). */
export function eParcare(doc: CanonicalDoc, st: CanonicalObject): boolean {
  if (st.properties.storey_kind === "parking") return true;
  const on = onStorey(doc, st.id);
  return (on.get("ParkingSpace") ?? []).length > 0
      || (on.get("Lane") ?? []).length > 0
      || ((st.display?.deck as unknown[] | undefined)?.length ?? 0) > 0;
}

export function toPlan(doc: CanonicalDoc, st: CanonicalObject): SolvedPlan {
  const diag = doc.diagnostics ?? {};
  const thetaDeg = diag.theta_deg ?? 0;
  const cell = diag.cell ?? 2.5;
  const level = st.properties.level ?? 0;
  const on = onStorey(doc, st.id);
  const road: number[][] = st.display?.road ?? [];

  const spaces: SolvedSpace[] = (on.get("ParkingSpace") ?? []).map((s, i) => ({
    // The canonical id, NOT a position hash: a bay that moves keeps its
    // identity, so React does not remount it.
    id: s.id,
    // Gabaritul din document (5,00 perpendicular, 5,75 paralel); un document
    // fără el primește locul perpendicular.
    cx: g(s, "cx"), cy: g(s, "cy"), w: g(s, "w") ?? 2.5, d: g(s, "h") ?? 5.0,
    deg: g(s, "deg"),
    kind: (s.properties.kind ?? "std") as SolvedSpace["kind"],
    n: s.display?.n ?? i + 1,
    form: s.properties.form ?? null,
  }));

  const ramps = on.get("Ramp") ?? [];
  // locurile fără cale de evacuare (§35) se desenează, dar nu se numără
  const faraEvacuare = (on.get("ParkingSpace") ?? []).filter((s) =>
    (s.props?.["architecture.evacuare"] as { fara_evacuare?: boolean } | undefined)?.fara_evacuare).length;
  return {
    status: diag.status ?? "done",
    count: spaces.length - faraEvacuare,
    cell,
    thetaDeg,
    roadCells: road.map(([x, y]) => ({ cx: x, cy: y, size: cell, deg: thetaDeg })) as RoadCell[],
    lanes: (on.get("Lane") ?? []).map((l) => {
      const ax = g(l, "axis");
      const w = (l.geometry as { w?: number } | undefined)?.w;
      const sursa = (l as { props?: Record<string, string> }).props?.["parcare.sursa"];
      return { eid: l.id, x1: ax[0][0], y1: ax[0][1], x2: ax[1][0], y2: ax[1][1],
               ...(typeof w === "number" ? { w } : {}),
               ...(sursa ? { sursa } : {}) };
    }),
    deck: st.display?.deck ?? [],
    spaces,
    columns: (on.get("Column") ?? []).map((c) => ({
      cx: g(c, "cx"), cy: g(c, "cy"), deg: g(c, "deg"),
      w: g(c, "w"), h: g(c, "h"),
    })) as SolverColumn[],
    rooms: (on.get("Space") ?? []).map((r) => ({
      kind: r.properties.kind, label: r.display?.label ?? r.properties.kind,
      // Measured, not stored: `area` was the exact shoelace of the ring beside
      // it on 6,021 objects. The `w * h` fallback is for a technical room given
      // as a rotated rectangle rather than a ring.
      area: shapeArea(r) || (g(r, "w") ?? 0) * (g(r, "h") ?? 0),
      cx: g(r, "cx"), cy: g(r, "cy"), w: g(r, "w"), h: g(r, "h"), deg: g(r, "deg"),
      ...(r.props?.["architecture.evacuare"] ? { evacuare: true } : {}),
    })) as SolverRoom[],
    level,
    // WHICH FLOOR OF WHICH BASEMENT this is. Four separate garages are four
    // plans, and keying them by level made three of them vanish.
    storey: st.id,
    owner: ownerOf(st),
    label: st.properties.name ?? t("nivelul.nr", { n: level }),
    // RAMPA INTERIOARĂ e cea de fel `ramp` (centru, lungime, unghi). Primul
    // `Ramp` de pe nivel era luat orbește, iar pe -1 primul e o rampă de ACCES,
    // cu poligon și fără centru — deci nu se desena nici rampa interioară, nici
    // cea de acces. Cele de acces își au acum rândul lor.
    rampInt: (() => {
      const r = ramps.find((q) => q.properties.kind === "ramp")
        ?? ramps.find((q) => g(q, "cx") != null);
      return r ? rampOf(r) : null;
    })(),
    // TOATE rampele interioare, nu doar prima: cu două accese sunt două rampe
    // spre -2, iar planul le arăta pe una singură (19.09.2026).
    rampsInt: ramps.filter((q) => q.properties.kind === "ramp" && g(q, "cx") != null)
                   .map(rampOf),
    accessRamps: ramps
      .filter((q) => q.properties.kind === "ramp_access" && (g(q, "poly") ?? []).length >= 3)
      .map((q) => ({ poly: g(q, "poly") as [number, number][],
                     mouth: g(q, "mouth") as [number, number][] | undefined,
                     // ce face rampa, nu doar unde stă: axa (gura → capătul de
                     // jos), panta, lungimea, sensul și podeaua de manevră
                     axis: g(q, "axis") as [number, number][] | undefined,
                     slope: g(q, "slope") as number | undefined,
                     runM: g(q, "run_m") as number | undefined,
                     twoWay: q.display?.two_way as boolean | undefined,
                     bottom: q.display?.bottom as [number, number] | undefined,
                     apron: q.display?.apron as [number, number][] | undefined,
                     helix: q.display?.helix as boolean | undefined,
                     geometry: q.display?.geometry as string | undefined,
                     axisPath: q.display?.axis_path as [number, number][] | undefined })),
    // Evacuation routing is no longer carried: it is derived from the aisles
    // and the SAS positions, both of which are in the document. The storey
    // keeps the one number that is not derivable by looking.
    evac: st.properties.evac_max_m != null
      ? { paths: [], max_m: st.properties.evac_max_m, routes: [] } : null,
    fireWalls: (on.get("Wall") ?? [])
      .filter((w) => w.properties.kind === "fire").map(fireWallOf),
    // la subsol singurii pereți de zidărie sunt cei de pe laturile rampelor -1→-2
    // Pereții de rampă sunt `partition` FĂRĂ `architecture.ala`: zidăria
    // grupului sanitar al unui adăpost e tot `partition`, dar nu e a rampei.
    rampWalls: (on.get("Wall") ?? [])
      .filter((w) => w.properties.kind === "partition"
        && !(w.props as Record<string, unknown> | undefined)?.["architecture.ala"]).map((w) => {
        const [[x1, y1], [x2, y2]] = g(w, "axis");
        return { x1, y1, x2, y2, t: g(w, "thickness_m") ?? 0.2 };
      }),
    fireDoors: (on.get("Door") ?? [])
      .filter((o) => o.properties.kind === "fire_door")
      .map((o) => ({ x: g(o, "cx"), y: g(o, "cy"), angleDeg: g(o, "deg") })) as FireDoor[],
    adaAisles: (on.get("AccessibleAisle") ?? []).map((a) => ({
      cx: g(a, "cx"), cy: g(a, "cy"), deg: g(a, "deg"), w: g(a, "w"), h: g(a, "h"),
    })),
  };
}

function rampOf(r: CanonicalObject) {
  return {
    eid: r.id,
    cx: g(r, "cx"), cy: g(r, "cy"), run: g(r, "run_m"), w: g(r, "width_m"),
    deg: g(r, "deg"), top: r.display?.top, bottom: r.display?.bottom,
    keep1: r.display?.keep_1 ?? [],
    keep2: r.display?.keep_2 ?? [],
    // felul continuării (S suprapusă, C în linie, P separată, E elicoidală),
    // ca desenul să poată spune ce fel de rampă e
    fel: r.display?.continuation as string | undefined,
    // corpul pe nivelul ăsta și curba, când rampa e elicoidală
    poly: r.display?.poly ?? undefined,
    helix: !!r.display?.helix,
    dedesubt: !!r.display?.dedesubt,
    center: r.display?.center ?? undefined,
    rInt: r.display?.r_int ?? undefined,
    rExt: r.display?.r_ext ?? undefined,
    sweepDeg: r.display?.sweep_deg ?? undefined,
    // cotită la 90° (K, SK): corpul în L și axul pe care merg galoanele
    geometry: r.display?.geometry as string | undefined,
    axisPath: r.display?.axis_path as [number, number][] | undefined,
  };
}

/** A fire wall, back in the shape the renderer draws. The label position lives
 *  under `display` in the document — inline, a moved label churned a semantic
 *  diff — and comes back out here because drawing is what this shape is for. */
function fireWallOf(w: CanonicalObject): FireWall {
  const [[x1, y1], [x2, y2]] = g(w, "axis");
  const [lx, ly] = w.display?.label_xy ?? [(x1 + x2) / 2, (y1 + y2) / 2];
  return { x1, y1, x2, y2, labelX: lx, labelY: ly,
           labelAngle: w.display?.label_deg ?? 0, label: !!w.display?.label };
}

/** Etajul de apartamente pe COTĂ — construitorul e `apartmentLevel`, iar
 *  intrarea folosită de ecran e `toApartmentLevelForStorey` de mai jos.
 *
 *  Varianta pe cotă a fost scoasă din uz: «toate corpurile» se desenează acum
 *  ca N planșee în cadrul lor comun, nu ca un singur model peste care se pun
 *  toate corpurile. Diferența conta: pe cotă, stratul de interioare aducea
 *  apartamentele tuturor celor patru corpuri peste planul unuia singur.
 */
/** Etajul de apartamente AL UNUI SINGUR PLANȘEU, nu al cotei.
 *
 *  Varianta pe COTĂ grupa pe ÎNĂLȚIME, peste toate corpurile, fiindcă o
 *  planșă de etaj a ansamblului la +9,00 desenează fiecare corp care ajunge
 *  acolo. Asta e corect câtă vreme desenul E al cotei. Ca STRAT peste planul
 *  unui corp, e greșit și se vede: planul desenează planșeul lui C2, iar
 *  stratul aducea apartamentele lui C1, C3 și C4 — desenate la coordonatele
 *  LOR, adică la zeci de metri în lateral. Pe ecran ieșea un strat care „n-are
 *  nicio legătură" cu restul, și chiar n-avea: era alt corp.
 *
 *  Aceeași funcție, altă sursă de obiecte: planșeul, nu cota. */
export function toApartmentLevelForStorey(doc: CanonicalDoc, storeyId: string):
    ApartmentLevel | null {
  const st = planseeSortate(doc).find((o) => o.id === storeyId);
  if (!st) return null;
  return apartmentLevel(doc, st.properties.level ?? 0, storeyId);
}

function apartmentLevel(doc: CanonicalDoc, level: number,
                        storeyId?: string): ApartmentLevel {
  const on = storeyId ? onStorey(doc, storeyId) : atLevel(doc, level);
  // THE BLOCKS THAT REACH THIS HEIGHT, found through their own floors.
  //
  // A Building is a STRUCTURE now — it has no storey, the storeys are its — so
  // it is not among the objects "on" a level. Which blocks a floor plan at
  // +9.00 draws is exactly "those with a storey at level 3", and a P+8 block
  // correctly drops out above 8.
  const here = planseeSortate(doc).filter((st) => storeyId
    ? st.id === storeyId
    : (st.properties.level === level
       && st.properties.storey_kind === "apartment"));
  const owners = new Set(here.map(ownerOf).filter(Boolean) as string[]);
  const blocks = deTip(doc, "Building").filter((o) => owners.has(o.id));
  // The per-object norm figures, by object id. They used to ride on the objects,
  // where nothing distinguished a REQUIREMENT from a measurement of the thing.
  const reqs = (doc.compliance?.requirements ?? {}) as
    Record<string, { min_area?: number; persons?: number;
                     useful_required?: number }>;

  const roomsByFlat = new Map<string, ApartmentRoom[]>();
  for (const r of on.get("Space") ?? []) {
    const flat = r.properties.flat;
    if (!flat) continue;
    const list = roomsByFlat.get(flat) ?? [];
    // A Space always carries a kind (kinds.TYPE says so); the `??` is for a
    // hand-made document that does not.
    const kind = r.properties.kind ?? "hall";
    // `eid` rides along with every view model built here. It is the document's
    // stable identity, and it is what the validator's report refers to — see
    // `ApartmentRoom.eid`. The array position stays too, because that is how the
    // renderer indexes; the id is what survives a re-solve.
    list.push({ eid: r.id, poly: g(r, "poly"), kind,
                label: r.display?.label ?? kind,
                // Measured from the ring; the norm floor comes from the rule
                // block, keyed by this room's id.
                area: shapeArea(r),
                min_area: reqs[r.id]?.min_area });
    roomsByFlat.set(flat, list);
  }

  const doorsByFlat = new Map<string, ApartmentDoor[]>();
  for (const o of on.get("Door") ?? []) {
    const flat = o.properties.flat;
    if (!flat) continue;
    const list = doorsByFlat.get(flat) ?? [];
    // `role` and `serves` are semantic and read from `properties`; the swing
    // geometry is drawing and reads from `display`.
    list.push({ eid: o.id, kind: o.properties.role ?? "door",
                label: o.properties.serves ?? o.display?.label ?? "",
                w: g(o, "width_m"), hinge: o.display?.hinge,
                tip: o.display?.tip, jambs: o.display?.jambs });
    doorsByFlat.set(flat, list);
  }

  const buildings = new Map(deTip(doc, "Building")
    .map((b) => [b.id, b.properties.code ?? b.id]));

  const flats: ApartmentFlat[] = (on.get("Flat") ?? []).map((f) => {
    const need = reqs[f.id];
    const all = roomsByFlat.get(f.id) ?? [];
    // The balcony is a room in the document and a field on the view model:
    // anexa 1 counts it separately, which is why A-37 lists S.U. and
    // S.U. TOTALĂ apart, and the renderer draws it in its own colour.
    // …TOATE balcoanele locuinței: fâșia compoziției e scrisă pe apartamente,
    // iar unul de colț are câte o bucată pe fiecare față. `balcony` rămâne
    // cel mai mare (de desenat/inspectat), cu ARIA tuturor.
    const balcoane = all.filter((r) => r.kind === "balcony" && (r.poly as number[][]).length >= 3);
    const balc = balcoane.length
      ? balcoane.reduce((m, r) => ((r.area ?? 0) > (m.area ?? 0) ? r : m), balcoane[0]) : undefined;
    const ariaBalcoane = balcoane.reduce((s, r) => s + (r.area ?? 0), 0);
    return {
      eid: f.id,
      poly: g(f, "poly"),
      // COUNTED and MEASURED, not read off the object.
      //
      // `rooms` meant HABITABLE rooms while `room_ids` listed nine spaces for a
      // three-room flat — two different things sharing a stem — and `area` was
      // the exact shoelace of `poly`. The norm figures (`persons`,
      // `useful_required`) are `norm(mix entry)` and live in
      // `compliance.requirements` under this flat's id, where a reader can tell
      // a requirement from a measurement of the thing.
      rooms: all.filter((r) => HABITABLE.has(r.kind)).length,
      area: shapeArea(f),
      persons: need?.persons,
      useful_required: need?.useful_required,
      label: f.display?.label ?? "", deg: g(f, "deg"),
      // The renderer labels by code ("C1"); the document links by id.
      building: buildings.get(f.properties.building ?? "") ?? undefined,
      frontage: g(f, "frontage"), wraps_core: f.properties.wraps_core,
      recomp: (f.props?.["architecture.recomp"] as
        { stare?: string; variante?: number; ales?: number; tip?: string } | undefined) ?? null,
      balcony: balc
        ? { poly: balc.poly as number[][], area: ariaBalcoane, eid: balc.eid }
        : null,
      balconies: balcoane.map((r) => ({ poly: r.poly as number[][], area: r.area ?? 0, eid: r.eid })),
      layout: all.filter((r) => r.kind !== "balcony"),
      doors: doorsByFlat.get(f.id) ?? [],
    };
  });

  // Core parts are top-level objects pointing back at their core, so they are
  // regrouped here rather than read out of a nested list.
  const partsByCore = new Map<string, CanonicalObject[]>();
  for (const p of on.get("CorePart") ?? []) {
    const core = p.properties.core;
    if (!core) continue;
    const list = partsByCore.get(core) ?? [];
    list.push(p);
    partsByCore.set(core, list);
  }

  const cores = (on.get("Core") ?? []).map((c) => ({
    eid: c.id,
    cx: g(c, "cx"), cy: g(c, "cy"), shared: c.properties.shared, deg: g(c, "deg"),
    building: buildings.get(c.properties.building ?? "") ?? null,
    parts: (partsByCore.get(c.id) ?? []).map((p) => ({
      eid: p.id,
      kind: p.properties.kind, label: p.display?.label ?? p.properties.kind,
      cx: g(p, "cx"), cy: g(p, "cy"), w: g(p, "w"), h: g(p, "h"),
      deg: g(p, "deg"), area: g(p, "area"), poly: g(p, "poly"),
    })),
  }));

  return {
    level,
    flats,
    // HOW MANY DWELLINGS ARE ON THIS FLOOR — counted from the floor.
    //
    // This was never set, so the level picker read "nedecis" for every
    // apartment storey in a document, including ones holding 56 flats. It is
    // `undefined` when there are none, which is the honest answer for a storey
    // an upstream stage placed and did not cut: nobody has decided, which is
    // not the same as none.
    count: flats.length || undefined,
    bars: (on.get("Bar") ?? []).map((b) => ({
      eid: b.id, poly: g(b, "poly"), corridor: g(b, "corridor"),
      building: buildings.get(b.properties.building ?? "") ?? null,
    })),
    cores,
    buildings: blocks.map((b) => ({
      // `id` falls back to the object's own id when the block has no `code`: an
      // upstream stage can place a volume before anybody has named it "C4", and
      // an undefined id makes the block unselectable rather than unnamed. `eid`
      // is the document id, which every view model here carries — see the note
      // above `ApartmentRoom.eid`.
      id: b.properties.code ?? b.id, eid: b.id, poly: g(b, "poly"),
      // COUNTED FROM THE OBJECTS ON THIS FLOOR, all of them. Nothing here is
      // read off the Building any more: `flats` claimed 288 dwellings over
      // storeys holding none, `cores` is `count(Core)`, `footprint_m2` is the
      // shoelace of `poly`, and `useful_m2` is a norm-dependent sum — which
      // makes it a compliance figure, and it is reported as one.
      flats: (on.get("Flat") ?? [])
        .filter((f) => f.properties.building === b.id).length || undefined,
      cores: (on.get("Core") ?? [])
        .filter((c) => c.properties.building === b.id).length || undefined,
      useful_m2: (doc.compliance?.reported as
        Record<string, { useful_m2?: number }> | undefined)?.[b.id]?.useful_m2,
      // Amprenta registrului (la fața finită, 7.10.2026) când arhitectura a
      // scris-o; altfel aria conturului, ca înainte.
      footprint_m2: (doc.compliance?.reported as
        Record<string, { footprint_m2?: number | null }> | undefined)?.[b.id]?.footprint_m2
        ?? shapeArea(b),
    })),
    walls: (on.get("Wall") ?? []).map((w) => ({
      key: w.properties.group ?? w.id, kind: w.properties.kind,
      axis: g(w, "axis"), thickness_m: g(w, "thickness_m"),
      height_m: g(w, "height_m"), bearing: w.properties.bearing,
    })),
    openings: (on.get("Window") ?? []).map((o) => ({
      wall: o.properties.wall, room: o.properties.room,
      u_m: g(o, "u_m"), width_m: g(o, "width_m"),
      height_m: g(o, "height_m"), sill_m: g(o, "sill_m"),
    })),
  } as unknown as ApartmentLevel;
}

/** BUCĂȚILE unui corp cu înălțimi diferite, cu înălțimea fiecăreia: bucata
 *  care urcă la regim are înălțimea corpului (`h`, la atic); una care se
 *  oprește cu `k` niveluri mai jos, cu `k × hNivel` mai puțin. Din
 *  `architecture.trepte` (după arhitectură) sau `site_plan.trepte` (planul de
 *  situație) — aceeași formă. `undefined` pe un corp uniform. */
function treptele(bld: CanonicalObject | undefined, h: number, hNivel: number):
    { pts: number[][]; h: number }[] | undefined {
  if (!bld) return undefined;
  const tp = ((bld.props?.["architecture.trepte"] ?? bld.props?.["site_plan.trepte"]) ?? null) as
    { storeys?: number; contururi?: number[][][]; niveluri?: number[] } | null;
  if (!tp || !Array.isArray(tp.contururi) || !Array.isArray(tp.niveluri)) return undefined;
  const n = Math.min(tp.contururi.length, tp.niveluri.length);
  if (n < 2) return undefined;
  const sus = Number.isFinite(tp.storeys) ? (tp.storeys as number) : Math.max(...tp.niveluri);
  // Bucata joasă stă cu `k` etaje sub cea de la regim, în aceeași convenție
  // ca înălțimea corpului (planul de situație dă `h` la terasă, cu 1,50 sub
  // atic): `h − k × hNivel`. Cu fațadă, `scenaSit` ridică fiecare bucată la
  // tavanul ultimului ei etaj, cum ridică și corpul.
  const out: { pts: number[][]; h: number }[] = [];
  for (let i = 0; i < n; i++) {
    const pts = (tp.contururi[i] ?? []).filter((q) => Number.isFinite(q?.[0]) && Number.isFinite(q?.[1]));
    const lv = tp.niveluri[i];
    if (pts.length < 3 || !Number.isFinite(lv)) continue;
    out.push({ pts, h: Math.max(hNivel, h - Math.max(0, sus - lv) * hNivel) });
  }
  return new Set(out.map((t) => t.h.toFixed(2))).size >= 2 ? out : undefined;
}

/** VOLUMELE SITULUI LA REGIMUL ARHITECTURII (22.10.2026).
 *
 *  Pe o situație îmbrăcată de o arhitectură, volumele vin din planul de
 *  situație (`site_plan.trepte`: pe Demo, C-ul din schiță cu o aripă la 15
 *  niveluri), iar fațada din arhitectură, care își pune treptele numai pe
 *  rosturi și a compus corpul altfel (bucăți la 12 și 13; pe alt corp, aripa
 *  înaltă în alt loc). Deasupra fațadei rămâneau etaje de volum alb, cu dungi
 *  acolo unde fețele coincideau (z-fight), și un acoperiș fără placaj. Aici
 *  fiecare corp ia BUCĂȚILE ARHITECTURII (`architecture.trepte`, în același
 *  cadru local ca situația — `docArh` vine adus la originea ei), cu
 *  înălțimile lor, și coboară la cea mai înaltă dintre ele; un corp uniform în
 *  arhitectură coboară la înălțimea ei. Fără arhitectură, sau pe un corp pe
 *  care ea nu-l are, nimic nu se schimbă. */
export function regimulArhitecturii(site: SiteLayer | null, docArh: CanonicalDoc | null): SiteLayer | null {
  if (!site || !docArh) return site;
  const corpuri = new Map<string, CanonicalObject>();
  for (const o of deTip(docArh, "Building")) {
    const c = o.properties.code as string | undefined;
    if (c) corpuri.set(c, o);
  }
  if (!corpuri.size) return site;
  // înălțimea de nivel a fiecărui corp, din planșeele lui supraterane (ca în `toSiteLayer`)
  const hNivel = new Map<string, number>();
  for (const st of planseeSortate(docArh)) {
    const own = ownerOf(st);
    const h = g(st, "height_m") as number | undefined;
    if (((st.properties.level as number) ?? -1) < 0) continue;
    if (own != null && Number.isFinite(h) && (h as number) > 0 && !hNivel.has(own)) hNivel.set(own, h as number);
  }
  const bars = site.bars.map((b) => {
    const o = b.code ? corpuri.get(b.code) : undefined;
    if (!o) return b;
    const hA = g(o, "height_m") as number | undefined;
    if (!Number.isFinite(hA) || (hA as number) <= 0) return b;
    // NUMAI bucățile arhitecturii: `site_plan.trepte` de pe corpul ei e copia
    // planului, adică exact ce nu vrem (pe un document de dinaintea tăierii
    // la regim ar ridica iar aripa pe care arhitectura n-a construit-o)
    const props = (o.props ?? {}) as Record<string, unknown>;
    const numaiArh: CanonicalObject = props["architecture.trepte"]
      ? { ...o, props: { "architecture.trepte": props["architecture.trepte"] } as CanonicalObject["props"] }
      : { ...o, props: {} as CanonicalObject["props"] };
    const trepte = treptele(numaiArh, hA as number, hNivel.get(o.id) ?? 3.0);
    const h = trepte ? Math.max(...trepte.map((t) => t.h)) : Math.min(b.h, hA as number);
    if (!trepte && h >= b.h - 1e-6) return b;
    const { trepte: _vechi, ...rest } = b;
    return { ...rest, h, ...(trepte ? { trepte } : {}) };
  });
  return { ...site, bars };
}


/** The on-grade layer, as the renderer's `SiteLayer`.
 *
 * The document has no `site` subtree: roads, pavements, green and surface bays
 * are objects like everything else, and the Site object carries the computed
 * totals. The renderer still wants them grouped, so they are regrouped here —
 * once, in the translate layer, rather than in the view.
 *
 * Building footprints come from the `Bar` objects on level 0. The document does
 * not repeat them under the site: two readable copies of one fact is what this
 * format exists to prevent, and it is what made `site.bars` disagree with the
 * storey's bars by 8.6%.
 */
export function toSiteLayer(doc: CanonicalDoc): SiteLayer | null {
  const site = deTip(doc, "Site")[0];
  if (!site) return null;
  const of = (t: ObjectType) => deTip(doc, t);
  const p = site.properties;

  // The volumes, as the stage that DECIDED them drew them.
  //
  // A document several stages filled can carry two versions of the massing: the
  // upstream one for the whole site, and a later stage's re-derivation of the
  // part it solved. Drawing both double-draws those volumes; drawing the later
  // one alone drew 2 buildings out of 15. `defaults.source` is the stage that
  // wrote this file, so anything with a different provenance came from further
  // up — and that is the one that decided the site.
  const ground = new Set(planseeSortate(doc)
    .filter((st) => st.properties.level === 0).map((st) => st.id));
  const bars0 = deTip(doc, "Bar")
    .filter((o) => ground.has(o.properties.storey ?? ""));
  const mine = doc.defaults?.source?.engine;
  const upstream = bars0.filter((b) => sourceOf(b, doc).engine !== mine);
  // PER BUILDING, not one height for the whole scheme. Every bar carries the
  // block it belongs to, and every block its own `height_m` and `code`; taking
  // the first building's height and stamping it on all of them labelled a
  // fifteen-storey block and a four-storey one identically.
  // …ȘI CONTURUL LUI, când îl are: corpul e al arhitecturii, iar pe o copie a
  // unui modul izolat ea îl desenează ca linia pereților, cu balcoanele afară,
  // pe când `Bar`-ul rămâne gabaritul planului de situație (plicul cu balcoane).
  // Macheta ridică soclul, volumele și aticul pe corp; pe bar, soclul ieșea
  // cât balconul în afara fațadei. Pe un corp compus din bare cele două coincid.
  const blocks = new Map<string, { h: number; code: string; poly?: number[][] }>();
  for (const o of deTip(doc, "Building")) {
    const poly = g(o, "poly") as number[][] | undefined;
    blocks.set(o.id, { h: (g(o, "height_m") as number) ?? 0,
                       code: (o.properties.code as string) ?? "",
                       ...(Array.isArray(poly) && poly.length >= 3 ? { poly } : {}) });
  }
  const anyBuilding = deTip(doc, "Building")
    .find((o) => o.geometry?.height_m != null);
  const fallbackH = anyBuilding ? (g(anyBuilding, "height_m") as number) : 0;
  // REGIMUL SE NUMĂRĂ, NU SE DEDUCE DIN ÎNĂLȚIME.
  //
  // Eticheta se refăcea din `height_m / 2.8 - 1`, dar motorul construiește cu
  // `BLD.FLOOR_HEIGHT = 3.00`. Cele două numere nu sunt aceeași convenție, iar
  // diferența se adună cu regimul: la 18 niveluri (P+17) h = 54.00 m, iar
  // `round(54 / 2.8) - 1` dă 18, deci planșa scria «P+18» pentru un bloc de
  // P+17. Raportat de beneficiar pe un P+17 (16.09.2026). Greșeala apare de la
  // 7 niveluri în sus și devine +2 de la 22.
  //
  // Nivelurile SUNT în document: `_building_storeys` scrie câte un `Storey` per
  // (clădire, nivel), fiecare cu `building` și `level` — chiar de aceea s-a
  // scos `levels` de pe `Building`, «those are in the document». Deci se
  // numără, iar 2.8 nu mai apare nicăieri.
  const etajul = new Map<string, number>();
  let etajulSite = -1;
  for (const st of planseeSortate(doc)) {
    const lv = st.properties.level as number | undefined;
    if (lv == null || lv < 0) continue;
    etajulSite = Math.max(etajulSite, lv);
    const own = ownerOf(st);
    if (own != null) etajul.set(own, Math.max(etajul.get(own) ?? 0, lv));
  }
  // ÎNĂLȚIMEA DE NIVEL a fiecărui corp, din planșeele lui (pentru bucățile
  // care se opresc mai jos): `height_m` al planșeului, altfel 3 m.
  const hNivel = new Map<string, number>();
  for (const st of planseeSortate(doc)) {
    const own = ownerOf(st);
    const h = g(st, "height_m") as number | undefined;
    // numai planșeele SUPRATERANE: un subsol de parcaj are 3,75 m, nu etajul
    if (((st.properties.level as number) ?? -1) < 0) continue;
    if (own != null && Number.isFinite(h) && (h as number) > 0 && !hNivel.has(own)) hNivel.set(own, h as number);
  }
  // SOCLUL: cât stă ±0,00 peste teren, aceeași cifră pe care o citește și
  // structura (cea mai mare dintre clădiri: terenul e unul)
  const ridicare = Math.max(0, ...deTip(doc, "Building").map((o) => {
    const v = g(o, "ground_floor_above_grade_m") as number | undefined;
    return Number.isFinite(v) ? (v as number) : 0;
  }));
  const bars = (upstream.length ? upstream : bars0).map((b) => {
    const id = (b.properties.building as string) ?? "";
    const blk = blocks.get(id);
    const h = blk?.h || fallbackH;
    // Fără niciun etaj al ei, clădirea cade pe cel mai de sus nivel al sitului:
    // așa arătau documentele de dinainte de 3.0, când toate corpurile împărțeau
    // aceleași etaje. Fără nici atât nu se scrie un regim inventat.
    const sus = etajul.get(id) ?? etajulSite;
    const trepte = treptele(blocks.get(id) ? indexul(doc).primulCuId.get(id) : undefined,
                            h, hNivel.get(id) ?? 3.0);
    return { pts: blk?.poly ?? g(b, "poly"), code: blk?.code ?? "",
             label: sus >= 0 ? `P+${sus}` : "", h,
             ...(trepte ? { trepte } : {}) };
  });

  return {
    bars,
    ...(ridicare > 0 ? { ridicare } : {}),
    roads: of("Road").map((r) => {
      // ARCELE AXEI (3.17, §36): cu `bulges` pe `axis`, axa se tesselează aici
      // (coarda la ≤ 0,05 m de arc) și merge în `path`, ca liniuțele axei să
      // urmeze curba; `roadAxis` sare drumurile cu `path`, deci un drum curb
      // nu primește și o axă dreaptă peste el. Asfaltul rămâne din `poly`,
      // pe care producătorul l-a tesselat deja.
      const ax0 = g(r, "axis") as number[][] | undefined;
      const bulges = g(r, "bulges");
      const ax = ax0 && areArce(bulges) ? teseleazaBulges(ax0, bulges) : ax0;
      // `two_way` and `kind` travel with the road. They were dropped, so the
      // drawing had to guess the direction from the width — and a 6 m one-way
      // aisle got two opposing lines of arrows painted on it.
      const meta = { two_way: r.properties.two_way !== false,
                     kind: (r.properties.kind as string) ?? "" };
      const piesa = ((r.props ?? {}) as Record<string, unknown>)["site_plan.piesa"] as
        { eticheta?: string; fel?: string; nota?: string } | undefined;
      const agent = idAgent(r);
      // găurile corpului (aleea-buclă închide o curte): fără ele curtea e asfalt
      const holes0 = g(r, "holes") as number[][][] | undefined;
      const holes = holes0 && holes0.length ? { holes: holes0 } : {};
      // a curved centreline (the S connector) keeps its polyline; p1/p2 are its ends
      // piesa agentului fără axă e un racord sau un pavaj, nu o alee: fără axă
      // de cutie peste ea (v. `SiteRoad.faraAxa`)
      return ax ? { p1: ax[0], p2: ax[ax.length - 1], ...(ax.length > 2 ? { path: ax } : {}),
                    poly: g(r, "poly"), ...holes, w: g(r, "w"), ...meta, ...(piesa ?? {}), ...agent }
                : { poly: g(r, "poly"), ...holes, w: g(r, "w"), ...meta, ...agent,
                    ...(agent.agent ? { faraAxa: true } : {}) };
    }),
    // WITH THEIR HOLES. An apron is a band round a building and the deep green
    // is grown in from the property line and stops at everything paved; handing
    // over the shell alone made the drawing claim the holes as surface.
    // FĂRĂ TRECERILE DE PIETONI (3.18, §37): o trecere e un `Sidewalk` peste
    // carosabil, dar umplută ca trotuar ar acoperi asfaltul; ea merge în
    // `crossings`, desenată zebră.
    // Trotuarele agentului poartă și axa (tesselată), lățimea și id-ul lui,
    // ca fișa să le poată numi; cele ale pasului 2 rămân simple suprafețe.
    sidewalks: of("Sidewalk").filter((o) => o.properties.kind !== "crossing").map((o) => {
      const sh = surface(o);
      const agent = idAgent(o);
      if (!agent.agent) return sh;
      const ax0 = g(o, "axis") as number[][] | undefined;
      const ax = ax0 && areArce(g(o, "bulges")) ? teseleazaBulges(ax0, g(o, "bulges")) : ax0;
      return { ...(Array.isArray(sh) ? { poly: sh } : sh), ...(ax ? { axis: ax } : {}),
               w: g(o, "width_m") as number | undefined, ...agent };
    }),
    crossings: of("Sidewalk").filter((o) => o.properties.kind === "crossing")
      .map((o) => {
        const trecere = ((o.props ?? {}) as Record<string, unknown>)["site_plan.trecere"] as
          { alee?: string } | undefined;
        return { poly: g(o, "poly") as number[][],
                 axis: g(o, "axis") as number[][] | undefined,
                 w: g(o, "width_m") as number | undefined,
                 ...idAgent(o), ...(trecere?.alee ? { alee: trecere.alee } : {}) };
      })
      .filter((c) => (c.poly?.length ?? 0) >= 3),
    // ACCESELE pe limita terenului (nu porți fizice): `Entrance` fel `access`, pe `Site`, fără
    // clădire. Felul (carosabil / pietonal) e al agentului, în props.
    gates: of("Entrance").filter((o) => o.properties.kind === "access")
      .map((o) => {
        const poarta = ((o.props ?? {}) as Record<string, unknown>)["site_plan.acces"] as
          { fel?: string } | undefined;
        return { x: g(o, "cx") as number, y: g(o, "cy") as number, deg: g(o, "deg") as number,
                 fel: poarta?.fel === "pietonal" ? "pietonal" as const : "carosabil" as const };
      })
      .filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y)),
    // ZONELE EXTERIOARE: `Area` cu felurile 3.18; un inel cu `bulges` (pe
    // `poly`, închis) se tesselează aici, ca să se umple arcul, nu coarda.
    zones: deTip(doc, "Area")
      .filter((o) => o.properties?.kind === "playground" || o.properties?.kind === "waste_platform"
                  || o.properties?.kind === "square")
      .map((o) => {
        const kind = o.properties.kind as "playground" | "waste_platform" | "square";
        const poly0 = g(o, "poly") as number[][];
        const bulges = g(o, "bulges");
        const poly = Array.isArray(poly0) && areArce(bulges)
          ? teseleazaBulges(poly0, bulges, 0.05, true) : poly0;
        return { kind, poly, label: LABEL[kind] ?? kind, ...idAgent(o) };
      })
      .filter((z) => (z.poly?.length ?? 0) >= 3),
    // BENZILE COMUNE PMR (3.18): `AccessibleAisle` de la sol — cu `site`, fără
    // `storey` (cele de subsol sunt ale planșeului lor, nu ale situației)
    pmrStrips: of("AccessibleAisle")
      .filter((o) => o.properties.site && !o.properties.storey)
      .map((o) => ({ poly: g(o, "poly") as number[][], ...idAgent(o) }))
      .filter((b) => (b.poly?.length ?? 0) >= 3),
    // documentul agentului: `diagnostics.agent` există numai pe ce scrie el
    ...(doc.diagnostics?.agent ? { agent: true } : {}),
    green: {
      deep: of("GreenArea").filter((o) => o.properties.kind === "green_deep")
        .map(surface),
      over_slab: of("GreenArea").filter((o) => o.properties.kind === "green_slab")
        .map(surface),
    },
    // copacii documentului: cei din topo (fără rază = coroană de 1,5 m, cât un
    // arbore tânăr) și cei puși de agent în fâșiile rândurilor (31.10.2026)
    trees: of("Tree")
      .map((o) => ({ x: g(o, "cx") as number, y: g(o, "cy") as number,
                     r: typeof g(o, "crown_radius_m") === "number" ? g(o, "crown_radius_m") as number : 1.5 }))
      .filter((t) => Number.isFinite(t.x) && Number.isFinite(t.y)),
    // BALCOANELE CARE IES DIN AMPRENTĂ.
    //
    // Un plan de situație desenează conturul construit la sol; balconul e la
    // etaj, deci nu apare — dar unul care se avântă peste amprentă schimbă
    // exact ce citește planul de situație: cât se apropie clădirea de limita de
    // proprietate. Cele care stau ÎN amprentă n-au ce adăuga și rămân afară.
    //
    // «Iese» = are cel puțin un colț în afara tuturor barelor de la sol. E un
    // test grosier — un balcon care iese fără să-și scoată vreun colț n-ar fi
    // prins — dar în arbore nu există bibliotecă de operații booleene pe
    // poligoane, iar un balcon se avântă întotdeauna cu colțuri cu tot.
    //
    // ACELAȘI balcon se repetă pe fiecare etaj: cincisprezece etaje înseamnă
    // cincisprezece poligoane identice, care desenate unul peste altul dau o
    // muchie neagră din simpla lor suprapunere. Se ține unul singur per poziție.
    balconies: balcoaneCareIes(doc, bars, false),
    // BALCOANELE DE LA PARTER, separat: nu sunt o proiecție de la etaj, sunt
    // la sol, deci se desenează cu linie plină (Oana, 28.09.2026). În planul de
    // situație ajung doar ele (`positioning_plan/backend/circulatie.balcoanele_parterului`).
    balconiiParter: balcoaneCareIes(doc, bars, true),

    // AMPRENTA SĂPĂTURII: conturul planșeelor de parcaj.
    //
    // Documentul n-are un obiect «excavație» — subsolul e o mulțime de planșee,
    // iar cât se sapă e conturul lor. Se ia de la planșeele de parcaj (v.
    // `eParcare`, care nu se încrede doar în `storey_kind`: un subsol vechi vine
    // fără el și se recunoaște după locuri, alei sau platelaj).
    //
    // Se ia UNUL per corp, nu toate planșeele: un subsol pe trei niveluri are
    // trei contururi aproape identice, iar suprapuse ar înnegri marginea prin
    // simpla lor repetare. Al mai mare dintre ele e cel care spune cât s-a
    // săpat, fiindcă săpătura le cuprinde pe toate.
    excavation: (() => {
      const peCorp = new Map<string, number[][]>();
      for (const st of planseeSortate(doc)) {
        if (!eParcare(doc, st)) continue;
        const inelul = inel(st);
        if (inelul.length < 3) continue;
        const cheie = ownerOf(st) ?? st.id;
        const acum = peCorp.get(cheie);
        if (!acum || ringArea(inelul) > ringArea(acum)) peCorp.set(cheie, inelul);
      }
      return [...peCorp.values()];
    })(),
    // One per side of the property line, in `side_index` order — what the
    // urbanism rules are counted per. `axis` is the side itself; the setbacks are
    // carried so the inspector can print what that side asks for without going
    // back to the document.
    edges: of("PlotEdge").map((o) => ({
      i: (o.properties.side_index as number) ?? 0,
      id: o.id,
      name: (o.properties.name as string) ?? "",
      kind: (o.properties.kind as string) ?? "",
      axis: g(o, "axis"),
      setback_urbanism_m: g(o, "setback_urbanism_m"),
      setback_fire_m: g(o, "setback_fire_m"),
      alignment_distance_m: g(o, "alignment_distance_m"),
      urbanism_is_assumed: !!o.properties.urbanism_is_assumed,
    })).sort((a, b) => a.i - b.i),
    // Surface bays have no storey: standing on grade rather than on a slab is
    // what tells them apart from a basement bay.
    // WITH THE FOOTPRINT. `deg` alone is not enough: the site-plan generator
    // writes a correctly rotated `poly` and leaves `deg` at 0 on every bay, so a
    // drawing built from `deg` lays a rotated scheme's parking out
    // axis-aligned — 94 overlapping pairs on a document that has none.
    stalls: of("ParkingSpace").filter((o) => o.properties.site)
      .map((o) => {
        // `valid: false` = loc roșu al agentului (3.18): se desenează, dar
        // marcat; lipsa câmpului înseamnă valid, ca pe documentele vechi
        const loc = ((o.props ?? {}) as Record<string, unknown>)["site_plan.loc"] as
          { valid?: boolean } | undefined;
        // `display.n` = numărul locului scris de agent, același ca pe planșa lui
        // (`data-nr`): omul și agentul vorbesc despre «locul 57» (31.10.2026)
        const n = o.display?.n;
        return { cx: g(o, "cx"), cy: g(o, "cy"), deg: g(o, "deg"),
                 poly: g(o, "poly"), kind: o.properties.kind,
                 ...(typeof n === "number" ? { n } : {}),
                 ...(loc?.valid === false ? { valid: false } : {}) };
      }),
    ramps: of("Ramp").map((o) => ({ poly: g(o, "poly"), slope: g(o, "slope"),
                                   mouth: g(o, "mouth"), width_m: g(o, "width_m"),
                                   // la rampa cotită axul e o polilinie (display)
                                   axisPath: o.display?.axis_path as number[][] | undefined }))
      .filter((r) => (r.poly?.length ?? 0) >= 3),
    // numai intrările CLĂDIRILOR: accesele de pe limita terenului (fel `access`,
    // 3.18) sunt în `gates`, altfel ar primi pridvor și săgeată de «acces clădire»
    entrances: of("Entrance").filter((o) => o.properties.kind !== "access").map((o) => ({
      x: g(o, "cx"), y: g(o, "cy"), deg: g(o, "deg"), porch: g(o, "poly"),
    })),
    // casele de scară de evacuare care ies la teren, de pe orice planșeu (pasul 2 le
    // copiază pe −1): `contur` e deja în cadrul desenului (`PROPS_WORLD_POINTS`)
    scariEvacuare: of("Space").flatMap((o) => {
      const ev = (o.props?.["architecture.evacuare"] ?? {}) as {
        scara?: string; contur?: number[][]; iesire_la_teren?: { zona_libera_m?: number };
        rampe?: number[][][]; desen?: { latime_m?: number; treapta_m?: number; podest_m?: number };
        tipar?: string; niveluri?: number[]; rezistenta?: string; persoane?: number; fluxuri?: number;
        latime_flux_m?: number;
      };
      if (o.properties.kind !== "casa_scarii_evacuare" || !ev.iesire_la_teren || (ev.contur?.length ?? 0) < 3) return [];
      return [{
        id: ev.scara ?? o.display?.label ?? o.id, contur: ev.contur as number[][],
        zonaM: ev.iesire_la_teren.zona_libera_m,
        rampe: (ev.rampe ?? []).filter((r) => r.length === 2 && r[0].length >= 2 && r[1].length >= 2)
          .map((r) => ({ de: [r[0][0], r[0][1]] as [number, number], la: [r[1][0], r[1][1]] as [number, number] })),
        latimeM: ev.desen?.latime_m, treaptaM: ev.desen?.treapta_m, podestM: ev.desen?.podest_m,
        tipar: ev.tipar, niveluri: ev.niveluri, rezistenta: ev.rezistenta, persoane: ev.persoane,
        fluxuri: ev.fluxuri, latimeFluxM: ev.latime_flux_m,
      }];
    }),
    usiBloc: usileBlocului(doc, bars),
    entries: p.entries ?? [],
    // The property line travels WITH the layer it bounds, so no consumer has to
    // guess which of the rings around is the lot.
    boundary: g(site, "poly"),
    // ZONA EDIFICABILĂ, așa cum a calculat-o MOTORUL — nu recalculată aici.
    // `export.zones` o scrie ca `Area` cu `kind: "buildable"` și o numește
    // „plicul LEGAL": exact ce rămâne din lot după retrageri. Ecranul o
    // folosește pe dos — hașurează restul, banda în care nu se poate construi —
    // fiindcă aia e verificarea care se face cu ochiul: corpurile trebuie să
    // cadă în partea nehașurată.
    edificabil: deTip(doc, "Area")
      .filter((o) => o.properties?.kind === "buildable")
      .map((o) => g(o, "poly") as number[][])
      .filter((r) => (r?.length ?? 0) >= 3),
    areas: (() => {
      // MEASURED, always. These were fields on the Site object that no
      // producer ever wrote, so every reader took the summing fallback anyway —
      // a field whose only live path is its fallback is a field that lies about
      // being available. `shapeArea` subtracts holes, which matters: the deep
      // green is a ring grown in from the property line and ignoring its holes
      // over-claimed it by 2.68x, against the figure the 20 % quota is checked
      // on.
      const planted = of("GreenArea");
      const deep = planted.filter((o) => o.properties.kind === "green_deep")
        .reduce((t, o) => t + shapeArea(o), 0);
      const slab = planted.filter((o) => o.properties.kind === "green_slab")
        .reduce((t, o) => t + shapeArea(o), 0);
      // Aria săpăturii: aceleași contururi ca `excavation` de mai sus, măsurate.
      // Se recalculează aici în loc să se citească de acolo fiindcă `areas` e
      // un bloc închis, cu o singură regulă — tot ce raportează e MĂSURAT.
      const sapat = (() => {
        const peCorp = new Map<string, number[][]>();
        for (const st of planseeSortate(doc)) {
          if (!eParcare(doc, st)) continue;
          const inelul = inel(st);
          if (inelul.length < 3) continue;
          const cheie = ownerOf(st) ?? st.id;
          const acum = peCorp.get(cheie);
          if (!acum || ringArea(inelul) > ringArea(acum)) peCorp.set(cheie, inelul);
        }
        return [...peCorp.values()].reduce((t, r) => t + ringArea(r), 0);
      })();
      return {
        lot: shapeArea(site),
        deep, over_slab: slab,
        excavation: sapat,
        // NOT deep + slab. Only a third of the green over a basement roof may
        // count toward the requirement, so a plain sum would report a
        // compliance figure nobody is entitled to. The credited share is
        // arithmetic on a norm and belongs to the rule engine, so this reports
        // the PHYSICAL total and leaves the crediting to whoever holds the norm.
        green_total: deep + slab,
      };
    })(),
    indicatori: indicatoriUrbanistici(doc),
    // varianta «fără curburi» a unei încercări: axa punctată rămâne dreaptă
    curburi: (doc.diagnostics?.circulatie?.varianta ?? "curburi") !== "fara_curburi",
  } as unknown as SiteLayer;
}

/** POT, CUT și regimul de înălțime — CITITE, nu recalculate.
 *
 *  Sunt cifre de conformitate: le stabilește motorul de reguli, care ține
 *  normele și știe cum se rotunjesc. Modelul canonic a scos dinadins
 *  `footprint_cap_m2` și `adc_cap_m2` (`pot × lot` și `cut × lot`) tocmai ca
 *  să nu existe două locuri care să răspundă diferit la aceeași întrebare —
 *  v. nota din `canonical_model/model.py`. Aici se ia verdictul ca atare.
 *
 *  Lipsa lor NU e o eroare: un document dinainte de verificări n-are
 *  `compliance`, iar legenda arată atunci doar suprafețele, ca până acum.
 */
function indicatoriUrbanistici(doc: any) {
  const reguli = (doc?.compliance?.rules ?? []) as {
    id?: string; value?: number; limit?: number | null; message?: string;
  }[];
  const ia = (id: string) => reguli.find((r) => r.id === id);
  const pot = ia("URB.POT"), cut = ia("URB.CUT"), niv = ia("URB.LEVELS");
  // Ariile construite, tot CITITE: motorul le scrie în `compliance.areas`,
  // fiindcă sunt de același fel cu P.O.T. și C.U.T. — nu se măsoară de pe
  // desen, se stabilesc acolo unde se știe ce se numără. Un document mai vechi
  // nu le are, și atunci rândurile lipsesc, ca și cum n-ar fi existat.
  const arii = (doc?.compliance?.areas ?? {}) as
    { built_above_m2?: number; built_below_m2?: number };
  if (!pot && !cut && !niv && arii.built_above_m2 === undefined) return undefined;
  return {
    supraterana: arii.built_above_m2,
    subterana: arii.built_below_m2,
    pot: pot && { valoare: pot.value, limita: pot.limit ?? undefined },
    cut: cut && { valoare: cut.value, limita: cut.limit ?? undefined },
    niveluri: niv && { valoare: niv.value, limita: niv.limit ?? undefined,
                       regim: niv.message },
  };
}


// ---------------------------------------------------------------------------
// Nivelul ca DESEN DE ARHITECTURĂ — pereți, goluri, spații
// ---------------------------------------------------------------------------
//
// A treia traducere, lângă `toPlans` (parcaje) și `apartmentLevel`
// (apartamente), și motivul pentru care există: cele două de dinainte citesc
// documentul cu vocabularul SOLVERULUI. Un loc de parcare, un apartament, un
// nucleu, o bară. Importul DWG scrie același format cu vocabularul unui plan de
// arhitectură — `Wall`, `Door`, `Window`, `Space`, `Slab`, `Column`, `Beam` —
// și nicio traducere nu-l citea: un nivel importat cu 31 de compartimentări, 15
// uși, 13 ferestre și 20 de camere ajungea pe ecran ca o pagină albă, fiindcă
// `apartmentLevel` caută `Flat`-uri, iar la import nu există niciunul.
//
// Aici nu se decide nimic: obiectele documentului se trec în forma pe care o
// desenează `StoreyView`, unul la unu. Inelul (`poly`) e datumul — importul îl
// scrie pentru fiecare perete și fiecare gol, tocmai fiindcă un dreptunghi
// recalculat din axă + grosime cade pe altă parte a rotunjirii.

/** Un obiect desenabil al nivelului: inelul lui și cine e. */
/** Din ce tronson a ieșit un apartament.
 *
 *  AUTORITAR e câmpul scris de generator (`architecture.flat.tronson`), care e
 *  chiar `type_code`-ul modulului — aceeași valoare pe care o poartă entitatea
 *  `Tronson`. Ecranul de arhitectură colorează apartamentele după el, ca să se
 *  vadă dintr-o privire că M1 iese la fel în toate blocurile.
 *
 *  PUNTE pentru documentele emise înainte să existe câmpul: modulul era scris
 *  și atunci, dar topit în etichetă — `"{corp}-{tronson}-{index}"`, v.
 *  `from_tronsoane`. Se ia penultima bucată, și numai dacă ultima e chiar un
 *  index; altfel o etichetă scrisă de om ar fi citită drept modul. Puntea
 *  există ca soluțiile deja salvate să se coloreze fără regenerare, și poate
 *  pleca odată cu ele. */
export function tronsonulApartamentului(o: CanonicalObject): string | undefined {
  const scris = (o.props?.["architecture.flat"] as
                 { tronson?: unknown } | undefined)?.tronson;
  if (typeof scris === "string" && scris.trim()) return scris.trim();
  const p = String(o.display?.label ?? "").split("-");
  return p.length >= 2 && /^\d+$/.test(p[p.length - 1]) && p[p.length - 2]
    ? p[p.length - 2] : undefined;
}

/** Al câtelea apartament al MODULULUI e: scris de generator pe copia unui modul
 *  izolat (`architecture.flat.apartament`), unde toate apartamentele vin din
 *  același modul și tronsonul nu le mai deosebește. */
export function apartamentulModulului(o: CanonicalObject): number | undefined {
  const n = (o.props?.["architecture.flat"] as { apartament?: unknown } | undefined)?.apartament;
  return typeof n === "number" && Number.isFinite(n) ? n : undefined;
}

/** O piesă de mobilier a nivelului (`Furniture`, 3.14): dreptunghiul ei rotit
 *  și ce scrie arhitectura în `props["architecture.piesa"]` — felul din paleta
 *  editorului, oglindirea, rangul de desen și rampele unei scări desenate,
 *  în cadrul piesei. Se desenează cu simbolurile editorului (`StoreyView`). */
export interface PiesaMobilier {
  eid: string;
  kind: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
  deg: number;
  ogl: [boolean, boolean];
  rang?: number;
  flat?: string;
  scara?: {
    rampe: { de: [number, number]; la: [number, number] }[];
    latime_m: number;
    podest_m?: number;
    treapta_m?: number;
    pas_m?: number;
    podeste?: { k: number; contur: [number, number][] }[];
  };
}

export interface StoreyShape {
  eid: string;
  kind: string;
  /** Eticheta pe care o scrie desenul, când obiectul poartă una. */
  label?: string;
  /** Codul din import — „S4", „F1", „U8" — cel cu care se caută în planșă. */
  code?: string;
  /** Din ce TRONSON a ieșit apartamentul — „M1", „M3". Numai pe `flatShapes`.
   *  V. `tronsonulApartamentului`. */
  tronson?: string;
  /** Al câtelea apartament al modulului (copia unui modul izolat) — v.
   *  `apartamentulModulului`. Numai pe `flatShapes`. */
  apartament?: number;
  /** Starea RECOMPARTIMENTĂRII apartamentului (`props["architecture.recomp"].stare`):
   *  `in_lucru` cât jobul de recompartimentare încă n-a scris camerele finale — planul
   *  îl desenează hașurat, cu „se compartimentează…". Pe `flatShapes` și, la parter, pe
   *  spațiul comun rămas dintr-o locuință sacrificată (`spaces`). */
  recomp?: string;
  poly: number[][];
  /** Inelele SCOASE din el, când obiectul e inelar.
   *
   *  Documentul le scrie (`geometry.holes`), dar forma desenată le pierdea, și
   *  un inel fără gaură e o PATĂ. Măsurat pe ultimul etaj al unui corp real:
   *  aticul — un inel de 25 cm în jurul conturului — ieșea ca poligon de
   *  1 040 m², adică toată placa, opac și desenat peste tot restul. Planul
   *  etajului era dedesubt, întreg, și nu se vedea nimic din el. */
  holes?: number[][][];
  axis?: number[][];
  /** ROLUL PLĂCII, din `props["architecture.slab"].rol` — azi numai `copertina`:
   *  tavanul unui balcon-cutie de dedesubt, scris de compoziția fațadei pe etajul
   *  de deasupra, la pardoseala lui. Planul îl desenează hașurat, ca terasă
   *  necirculabilă. Numai pe `slabs`. */
  rol?: string;
  thickness_m?: number;
  width_m?: number;
  height_m?: number;
  /** Parapetul unui gol — cota de jos față de pardoseala nivelului. Vine din
   *  document (măsurat sau «template_default»); când lipsește, macheta pune
   *  convenția ei. */
  sill_m?: number;
  /** TÂMPLĂRIA golului, din `props["architecture.opening"].joinery` (regula
   *  `window` a stilului sau implicitul emitentului): câte canate are și cât
   *  de lat e profilul tocului. Macheta le DESENEAZĂ, nu le hotărăște. */
  canate?: number;
  profil_m?: number;
  culoare_toc?: string;
  /** TOATĂ tâmplăria scrisă pe gol — secțiunea tocului, canatele cu felul
   *  fiecăruia, montantul, geamul, mânerul, pragul — din care macheta o
   *  construiește (v. `plan/tamplarie3d`). */
  tamplarie?: TamplarieGol;
  /** FOILE UȘII: 2 pe o ușă `double` (`props["architecture.opening"].model`),
   *  altfel 1. Planul desenează câte un canat și un arc pentru fiecare. */
  foi?: number;
  /** UȘA CU PARTE FIXĂ (`model: sidelight`): lățimea foii care se deschide;
   *  restul golului e geam fix în același toc. */
  foaie_m?: number;
  /** ÎN CARE TOC E BALAMAUA — `end` = capătul de sfârșit al direcției
   *  golului (`deg`), altfel cel de început. */
  balama?: "start" | "end";
  /** GEAMUL PE COLȚ: id-ul golului de pe fața vecină cu care se întâlnește în
   *  colțul clădirii (`props["architecture.opening"].corner.with`). */
  colt?: string;
  /** GOLUL DIN BETON din spatele unui perete cortină
   *  (`props["architecture.opening"].cortina`): gaura rămâne, tâmplăria nu —
   *  geamul e celula cortinei. Macheta nu-i desenează toc. */
  inCortina?: boolean;
  cx?: number;
  cy?: number;
  deg?: number;
  /** Aria măsurată din inel; nimic derivat nu se citește din document. */
  area?: number;
  /** Pe o cameră exterioară: `balcony` (în consolă) sau `loggia` (scobită în
   *  volum), din `props["architecture.space"].outdoor`. Decide dacă macheta
   *  scoate o placă în afară sau taie o adâncitură în corp. */
  outdoor?: string;
  /** Apartamentul din care face parte, când documentul o spune (`properties.flat`).
   *  Se poartă mai departe ca să se poată desena camera ÎN apartamentul ei —
   *  legătura e pe id, nu ghicită din geometrie. */
  flat?: string;
  /** Numai pe o casă de scară pe care limita de evacuare a cumpărat-o: punctul
   *  care a cerut-o. Vezi `evacDriver`. */
  evacDriver?: EvacDriver;
  /** Ce spune despre obiect pasul ALA al parcării (`props["architecture.ala"]`):
   *  al cărui adăpost e, ce rol are și, pe adăpost, detaliile arătate la click;
   *  pe grupul sanitar, WC-urile și lavoarele lui. */
  ala?: AlaProps;
  /** Holul SAS al căilor de evacuare (`props["architecture.evacuare"]`). */
  evacuare?: boolean;
  /** Treptele casei de scară de evacuare a parcajului (`casa_scarii_evacuare`,
   *  formatul 3.16, §35): rampele (de jos în sus, pe axa lor) și dimensiunile,
   *  în forma scării desenate din mobilier, ca planșa să le deseneze la fel. */
  scaraEvacuare?: { rampe: { de: [number, number]; la: [number, number] }[];
                    latime_m: number; podest_m?: number; treapta_m?: number; pas_m?: number };
  /** Locul de parcare FĂRĂ cale de evacuare sub 40 m (§35): desenat roșu, nu se
   *  numără. */
  faraEvacuare?: boolean;
  /** Rezistența la foc a unui perete antifoc, așa cum a scris-o generatorul
   *  care l-a făcut: «EI 120» pe compartimentarea parcajului
   *  (`architecture.fire`), «EI 120» / «EI 180» pe încercuirea nucleului
   *  (`architecture.nucleu`), «EI 120» pe holul SAS (`architecture.evacuare`).
   *  Un fapt, scris de cine construiește peretele; interfața doar îl arată. */
  rezistenta?: string;
}

/** Rezistența la foc de pe obiect: cheia `rezistenta` din spațiul de `props`
 *  al generatorului care l-a făcut (`architecture.fire`, `architecture.nucleu`,
 *  `architecture.evacuare`), oricare ar fi el — fiecare pas își scrie
 *  peretele în spațiul lui, dar faptul e același. */
export function rezistentaLaFoc(props: CanonicalObject["props"]): string | undefined {
  for (const [ns, v] of Object.entries((props ?? {}) as Record<string, unknown>)) {
    if (!ns.startsWith("architecture.")) continue;
    const r = (v as { rezistenta?: unknown } | null | undefined)?.rezistenta;
    if (typeof r === "string" && r) return r;
  }
  return undefined;
}

/** `props["architecture.ala"]`, cum îl scrie `architecture/ala/pas.py`. Centrele
 *  și direcțiile obiectelor sanitare au trecut deja prin cadrul desenului
 *  (`PROPS_WORLD_POINTS` / `PROPS_WORLD_DIRECTIONS`). */
export interface AlaProps {
  adapost?: string;
  rol?: string;
  persoane?: number;
  cladire?: string | null;
  bucata?: string;
  mod?: string;
  arie_m2?: number;
  utila_m2?: number;
  adapostire_m2?: number;
  necesar_m2?: number;
  /** De ce are adăpostul mărimea asta: aria pe persoană e cea mai mare dintre
   *  limita de arie și volumul pe persoană împărțit la înălțimea luată în
   *  calcul; ținta felierii e un adăpost plin plus SAS și grup sanitar. */
  dimensionare?: {
    h_m: number; mc_pe_persoana: number; mp_pe_persoana_arie: number;
    mp_pe_persoana: number; leaga: "volum" | "arie"; capacitate: number;
    sas_m2: number; grup_sanitar_m2: number; tinta_m2: number;
  };
  sas?: string;
  grup_sanitar?: string;
  iesiri?: string[];
  /** Sensul ieșirii unei sărituri de lup, dinspre adăpost spre exterior (versor,
   *  în lume): după el planșa așază cotele puțului — v. `coteNivel`. */
  spre?: number[];
  /** Pe un gol al adăpostului: indicativul tâmplăriei din normativ (UME1,
   *  UME2, UE, OME1) și pragul lui, când are (0,15 m la ușile SAS-ului și la
   *  oblon). Se scriu pe planșă, cu linie de indicație spre gol. */
  gol?: string;
  indicativ?: string;
  prag_m?: number;
  aceeasi_fatada?: boolean;
  locuri_pierdute?: number;
  neconcordanta?: string;
  cabine?: number;
  lavoare?: number;
  obiecte?: { fel: string; w: number; h: number }[];
  obiecte_c?: number[][];
  obiecte_dir?: number[][];
  etans?: boolean;
}

/** Un panou de fațadă — un `FacadePanel` din document, gata de extrudat.
 *
 *  Panoul stă pe o FAȚĂ a corpului (nu pe un perete): `a`→`b` e segmentul lui
 *  la sol, `n` normala care iese din clădire (de pe obiectul `Facade`), `z0`/`z1`
 *  cotele față de pardoseala nivelului, `depth_m` cât iese fața lui exterioară
 *  din planul zidăriei și `thickness_m` cât e gros. Un intrados de balcon
 *  (`surface: soffit`) vine cu `poly`, nu cu segment. Macheta îl desenează
 *  exact așa — nimic dedus din înălțime sau din culoarea peretelui. */
export interface PanouFatada {
  eid: string;
  kind: string;
  layer: string;
  surface: string;
  face?: string;
  a: number[];
  b: number[];
  n?: number[];
  poly?: number[][];
  z0: number;
  z1: number;
  depth_m: number;
  thickness_m: number;
  material?: string;
  colour?: string;
  /** Secțiunea unui ORNAMENT CU VOLUM, în metri, în planul (adâncime de la fața
   *  finisajului, înălțime de la baza lui) — din `props["architecture.panel"]
   *  .section`. Fără ea panoul e o cutie. */
  section?: number[][];
  /** CALEPINAJUL unui placaj (format 3.6, §24): plăcile `[u0, u1, z0, z1]` —
   *  `u` de la `a`, `z` de la pardoseala etajului —, lățimea rostului și ce se
   *  vede prin el. Lipsă = panou întreg. */
  rosturi?: { latime: number; culoare: string; placi: number[][];
              /** Plăcile în altă culoare (`joints.accents`, regula `tile`): indicele
               *  plăcii în `placi` → culoarea ei. */
              accente?: Record<number, string> };
  /** PARDOSEALA (format 3.8, §26): piesele unui panou `flooring` de pe un
   *  balcon, o logie sau o terasă, în reperul scris ca indici în `poly`. */
  pardoseala?: Pardoseala;
  /** PERETELE CORTINĂ (format 3.10, §28): grila panoului `curtain_wall`. */
  cortina?: CortinaPanou;
  /** Unde începe panoul pe FAȚA lui (`props["architecture.panel"].u0`): cu el
   *  macheta trece un punct din lume în metri pe față — zona aleasă pe machetă. */
  u0?: number;
}

/** Balustrada unui balcon — `Railing`: polilinia ei la cota nivelului și
 *  înălțimea; `kind` (glass|slats|solid) e ce schimbă cum se desenează. */
export interface ParapetFatada {
  eid: string;
  kind: string;
  axis: number[][];
  height_m: number;
  /** de la ce cotă PESTE pardoseală pornește: balustrada unei terase stă pe
   *  bordura ei zidită (v. `fatada.parapet_terasei`) */
  sill_m?: number;
  /** balcony | loggia | terrace — al cărui spațiu exterior e (props) */
  outdoor?: string;
  face?: string;
  material?: string;
  colour?: string;
  /** GROSIMEA, la o jardinieră (`kind: "planter"`, format 3.5): un parapet
   *  plin în care se plantează. Lipsă la celelalte balustrade. */
  thickness_m?: number;
  /** ECRANUL FIX (`kind: "screen"`): desenul lui (perforated | slats) și cât
   *  din el e gol — din `props["architecture.railing"]`. */
  pattern?: string;
  openness?: number;
  /** CE CREȘTE ÎNTR-O JARDINIERĂ (`kind: "planter"`): mixed | shrubs | grasses |
   *  hanging | none — numai pentru desen; lipsă = mixed. */
  planting?: string;
  /** MÂNA CURENTĂ (format 3.9, §27): obiectul ei; `null` = scoasă din stil;
   *  lipsă = document mai vechi (se desenează bara de dinainte). */
  handrail?: ManaCurenta | null;
  /** PROFILUL DE STICLĂ de la bază (§27): șina în care stă prinsă sticla;
   *  `null` = scos din stil; lipsă = document mai vechi (fără profil). */
  profil?: { width_m: number; height_m: number; material?: string; colour?: string } | null;
}

/** Un nivel al unei structuri, cu tot ce stă desenat pe el.
 *
 *  Cheia e POVESTEA nivelului, nu numărul lui: două subsoluri separate sunt
 *  două desene, iar `id` e al planșeului, nu al cotei. */
/** Un reper desenat pe planul de nivel: amprenta unei clădiri sau limita
 *  terenului, proiectate din alt plan ca să se vadă cum stă nivelul.
 *
 *  `code` e codul din planșă („C1") pentru o clădire; limita terenului n-are
 *  unul. */
export interface Reper {
  poly: number[][];
  code?: string;
}

/** Vezi `evacDriver`. `walk_m: null` înseamnă că punctul n-avea niciun traseu. */
export interface EvacDriver {
  point: [number, number];
  /** Cât se mergea de la punct până la cea mai apropiată ieșire FĂRĂ scara
   *  asta — cifra care depășea limita. `null` = n-avea niciun traseu. */
  walk_m: number | null;
  /** ...și cât se merge CU ea. */
  walk_new_m: number | null;
  limit_m: number | null;
  /** Toleranța cu care solverul a comparat plimbarea cu norma. Vine DIN
   *  document (`EVAC_TOL`), nu scrisă de mână aici: rasterul de evacuare are
   *  pasul de 1,5 m, deci o distanță măsurată pe el nu poate fi la centimetru,
   *  iar fără cifra asta «42,2 m contra limitei de 42,0» s-ar citi ca o
   *  încălcare — deși solverul consideră schema conformă. */
  tol_m: number | null;
  /** TRASEELE MERSE, pe alei, nu linii drepte: verde până la scara asta, roșu
   *  până la cea mai apropiată ieșire de dinainte. Linia dreaptă minte — un
   *  punct poate părea lângă o scară și fi dincolo de un perete de
   *  compartiment, iar diferența aia e chiar motivul scării. */
  path_new: number[][];
  path_old: number[][];
}

export interface StoreyPlan {
  id: string;
  level: number;
  /** `storey_kind` din document — `basement_storey`, `ground`, `apartment`… */
  kind: string;
  owner?: string;
  /** Codul corpului care deține planșeul («C1»), pentru selectorul de corp. */
  cod?: string;
  /** Cum se numește nivelul în interfață: „Subsol 1", „Parter", „Etaj 2". */
  label: string;
  /** Cota planșeului, în metri, CITITĂ din document — nu `level × înălțime`.
   *
   *  Fiecare planșeu își declară cota lui, iar regimul de înălțime nu e
   *  uniform: sub parter stau două subsoluri de parcaj de 3 m, deasupra etaje
   *  de 2,9. `undefined` când documentul n-o scrie, și atunci nu se afișează —
   *  o cotă ghicită pe un plan e mai rea decât una lipsă. */
  cota?: number;
  outline: number[][];
  /** PLĂCILE nivelului (format 3.4, `Storey.geometry.parts`): pe un corp cu
   *  înălțimi diferite, deasupra unei bucăți oprite ÎNTRE două care urcă,
   *  planșeul stă în două bucăți separate, iar `outline` e doar cea mai mare.
   *  Cine desenează sau măsoară planșeul o face pe `parts`; `[outline]` când
   *  planșeul e întreg, gol când n-are contur deloc. */
  parts?: number[][][];
  /** REPERELE, pentru un nivel subteran: unde ar fi clădirile și unde e limita
   *  terenului.
   *
   *  Un subsol săpat până la 2 m de limita de proprietate depășește cu mult
   *  amprentele de deasupra, și fără repere planul lui nu se poate citi — nu se
   *  vede nici cât iese din clădiri, nici cât mai e până la vecin. Sunt
   *  PROIECȚII din alt plan, deci se desenează întrerupt, ca `SiteLayer.
   *  balconies`; iar oglinda lor există deja — `SiteLayer.excavation` desenează
   *  subsolul pe planșa de situație.
   *
   *  Amprentele sunt ale obiectelor `Building`, deci NU includ `Area`: aria de
   *  subsol e chiar nivelul pe care stăm, iar desenată ca reper de clădire ar
   *  fi absurdă. Se umplu numai pe nivelurile de parcaj — la etaje conturul
   *  clădirii ESTE planșeul, deci reperul n-ar spune nimic, iar un strat cu zero
   *  nu se listează. */
  refBuildings: Reper[];
  refPlot: number[][];
  slabs: StoreyShape[];
  walls: StoreyShape[];
  /** Pereții care ÎNCEP DEASUPRA TĂIETURII planșei (`sill_m` ≥ 1,20 m), scoși
   *  din `walls` fiindcă planul de arhitectură e o secțiune orizontală și ei
   *  n-o taie (v. `peste_sectiune`). Aici, fiindcă MACHETA îi are: aticul
   *  acoperișului e un asemenea perete — inelul lui, tăiat la terasele
   *  ultimului etaj, e chiar conturul pe care macheta îl ridică. Fără lista
   *  asta ea cădea pe amprenta întreagă a corpului și punea atic și în jurul
   *  teraselor, în aer (măsurat pe documentul beneficiarului, 21.09.2026). */
  wallsAboveCut: StoreyShape[];
  openings: (StoreyShape & { tip: "Door" | "Window" })[];
  spaces: StoreyShape[];
  columns: StoreyShape[];
  beams: StoreyShape[];
  /** Scările fixe (`Ladder`): azi scara de pisică din săritura de lup a unui
   *  adăpost ALA, un dreptunghi cât ies treptele din peretele din fund. */
  ladders: StoreyShape[];
  /** TREPTELE dintre două cote ale aceluiași etaj (`Stair`, 3.12): de la o
   *  placă la altă cotă (`Slab` de fel `offset`, în `slabs`, cu `sill_m` față
   *  de pardoseala etajului) până la pardoseală — intrarea de la teren. `deg`
   *  e sensul urcării, `sill_m` de unde pornesc, `height_m` cât urcă. */
  stairs?: StoreyShape[];
  /** PLATFORMA ELEVATOARE de lângă trepte (`Lift`, 3.12). */
  lifts?: StoreyShape[];
  /** Vocabularul parcajului, ca GEOMETRIE — nu doar numărat în `parking`.
   *
   *  Până acum din tot un subsol ajungea în plan un singur întreg, iar locurile,
   *  aleile, rampa și culoarele PMR erau citite EXCLUSIV de `toPlan`, adică de
   *  desenul de parcaj. Filele „2D" și „3D" nu citesc `SolvedPlan`, ci planul de
   *  aici — deci pe un subsol nu aveau ce arăta și randau o placă goală cu două
   *  pastile de nucleu, singurele `Space` de acolo. Nu era o eroare de desen: era
   *  o lipsă de date, cu un nume care spunea că datele există (`parking: 42`). */
  bays: StoreyShape[];
  lanes: StoreyShape[];
  ramps: StoreyShape[];
  aisles: StoreyShape[];
  /** Conturul fiecărui apartament — strat, nu vedere separată. */
  flatShapes: StoreyShape[];
  /** Câte goluri n-au putut fi așezate: nici inel, nici `cx/cy`, nici
   *  `wall` + `u_m`. Se spune, nu se ascunde — v. `golul`. */
  fara_loc: number;
  /** Părțile nucleului — casa scării, ascensoarele, ghena, holul de nivel —
   *  ca FORME, cu `kind`-ul lor, ca planul să le deseneze ca pe camere.
   *
   *  Pe un planșeu generat cu apartamente, nucleul NU e scris ca `Space`, ci
   *  ca `CorePart` (vocabularul solverului), și planul de arhitectură îl
   *  ignora: etajele ieșeau fără scară și fără lift, deși subsolul — unde
   *  aceleași lucruri sunt `Space`-uri — le arăta. Traduse aici în aceeași
   *  formă ca spațiile, se desenează, se etichetează și intră în legendă la
   *  fel pe orice nivel. */
  cores: StoreyShape[];
  /** Mobilierul desenat în module, copiat în soluție (`Furniture`, 3.14).
   *  Gol pe documentele de dinainte și pe nivelurile de parcaj. */
  mobilier?: PiesaMobilier[];
  /** Nucleele și barele nivelului, NUMĂRATE, pentru panoul de straturi. Barele
   *  rămân vocabularul solverului, desenat de `ApartmentView`. */
  nuclee: number;
  bare: number;
  /** Câte camere au un simbol de mobilier de desenat. Nu „câte camere": o baie
   *  de 1,2 m² și un living de 24 primesc amândouă mobilier, dar un hol nu, iar
   *  bifa trebuie să spună câte lucruri aprinde. */
  mobilate: number;
  /** Câte locuri de parcare și câte apartamente stau pe nivel, și verdictul
   *  „e parcaj" (v. `eParcare`) — atât cât să se aleagă desenul potrivit, fără
   *  să se ghicească după `storey_kind`. */
  parking: number;
  parcare: boolean;
  flats: number;
  /** Fațada nivelului, din document: panourile și balustradele derivate din
   *  regulile soluției (`layout.fatada`). Opționale, fiindcă un document de
   *  dinainte de fațadă n-are niciunul — și atunci macheta desenează volumul
   *  curat, ca până acum. */
  panouri?: PanouFatada[];
  parapete?: ParapetFatada[];
}

/** Numele nivelului, așa cum îl cere un plan de arhitectură.
 *
 *  „Parcare · nivel −1" e numele bun pentru un subsol PLIN DE LOCURI, și era
 *  singurul: un subsol importat, cu pereți și camere, apărea tot ca parcare —
 *  când apărea. Numele vine din cotă, nu din `storey_kind`, fiindcă exact asta
 *  citește omul pe planșă: Subsol 1, Parter, Etaj 2. */
export function numeNivel(level: number, parcare: boolean): string {
  if (parcare) return t("nivelul.parcare", { n: level });
  if (level < 0) return t("nivelul.subsol", { n: -level });
  if (level === 0) return t("nivelul.parter");
  return t("nivelul.etaj", { n: level });
}

/** Camerele care primesc un simbol de mobilier, exact cele pe care le știe
 *  `ApartmentView.Furniture`. Lista e aici fiindcă numărătoarea stratului se
 *  face aici; ea și `switch`-ul de acolo trebuie schimbate împreună. */
const MOBILATE = new Set(["living", "bedroom", "kitchen", "bath", "bath2",
                          "wc", "shower", "store"]);

const inel = (o: CanonicalObject): number[][] => (g(o, "poly") ?? []) as number[][];

/** Conturul planșeului, iar când nu-l are — al obiectului care îl DEȚINE (v.
 *  nota de la `outline` în `toStoreyPlans`). */
const conturPlanseu = (st: CanonicalObject, amprente: Map<string, number[][]>): number[][] =>
  inel(st).length >= 3 ? inel(st) : (amprente.get(ownerOf(st) ?? "") ?? []);

/** Plăcile planșeului: `geometry.parts` când sunt scrise (două sau mai multe
 *  inele), altfel conturul singur. Niciodată goală câtă vreme există un contur. */
const placilePlanseului = (st: CanonicalObject, amprente: Map<string, number[][]>): number[][][] => {
  const scrise = (st.geometry as { parts?: unknown } | undefined)?.parts;
  const ok = Array.isArray(scrise)
    ? (scrise as unknown[]).filter((r): r is number[][] => Array.isArray(r) && r.length >= 3) : [];
  if (ok.length >= 2) return ok;
  const c = conturPlanseu(st, amprente);
  return c.length >= 3 ? [c] : [];
};

const forma = (o: CanonicalObject): StoreyShape => ({
  eid: o.id,
  kind: o.properties.kind ?? "",
  ...((o.props?.["architecture.slab"] as { rol?: string } | undefined)?.rol
    ? { rol: (o.props?.["architecture.slab"] as { rol: string }).rol } : {}),
  label: o.display?.label ?? undefined,
  code: o.properties.code ?? undefined,
  poly: inel(o),
  holes: (g(o, "holes") as number[][][] | undefined)?.filter((h) => h.length >= 3),
  axis: g(o, "axis"),
  thickness_m: g(o, "thickness_m"),
  width_m: g(o, "width_m"),
  height_m: g(o, "height_m"),
  sill_m: g(o, "sill_m"),
  ...tamplaria(o),
  ...((o.props?.["architecture.opening"] as { cortina?: boolean } | undefined)?.cortina
    ? { inCortina: true } : {}),
  cx: g(o, "cx"), cy: g(o, "cy"), deg: g(o, "deg"),
  area: shapeArea(o),
  flat: (o.properties.flat ?? undefined) as string | undefined,
  evacDriver: evacDriver(o),
  ala: (o.props as Record<string, AlaProps | undefined> | undefined)?.["architecture.ala"],
  rezistenta: rezistentaLaFoc(o.props),
  // holul SAS al căilor de evacuare (`architecture/evacuare/pas.py`): se
  // desenează ca adăpostul, transparent, cu pereții și ușile peste el
  ...(o.props?.["architecture.evacuare"] ? { evacuare: true } : {}),
  ...scaraEvacuare(o),
  ...((o.props?.["architecture.evacuare"] as { fara_evacuare?: boolean } | undefined)?.fara_evacuare
    ? { faraEvacuare: true } : {}),
});

/** Treptele unei case de scară de evacuare (§35), din `props["architecture.evacuare"]`:
 *  `rampe` ca perechi de puncte (mutate deja cu planul, `PROPS_WORLD_POINTS`) și
 *  `desen` cu dimensiunile. */
function scaraEvacuare(o: CanonicalObject): Pick<StoreyShape, "scaraEvacuare"> {
  if (o.properties.kind !== "casa_scarii_evacuare") return {};
  const ev = o.props?.["architecture.evacuare"] as
    { rampe?: number[][][]; desen?: { latime_m?: number; podest_m?: number; treapta_m?: number; pas_m?: number } } | undefined;
  const rampe = (ev?.rampe ?? []).filter((r) => r.length === 2 && r[0].length >= 2 && r[1].length >= 2);
  if (!rampe.length || !ev?.desen?.latime_m) return {};
  return { scaraEvacuare: {
    rampe: rampe.map((r) => ({ de: [r[0][0], r[0][1]] as [number, number], la: [r[1][0], r[1][1]] as [number, number] })),
    latime_m: ev.desen.latime_m, podest_m: ev.desen.podest_m, treapta_m: ev.desen.treapta_m, pas_m: ev.desen.pas_m } };
}

/** Punctul care a CERUT scara asta, când limita de evacuare a cumpărat-o.
 *
 *  `cores.place` adaugă scări suplimentare de garaj când nucleele blocurilor nu
 *  acoperă pardoseala în 42 m (21 m pe capăt de sac), și le așază DELIBERAT în
 *  afara amprentei corpurilor — deci pe planșă apare o casă de scară sub care nu
 *  stă nicio clădire. Câmpul spune care punct a cumpărat-o, cât se mergea de
 *  acolo cu scările care existau deja, și limita în vigoare atunci.
 *
 *  Stă în `props`, nu în `properties`: e un detaliu al acestui obiect și un caz
 *  marginal — câteva scări dintr-un garaj — deci nu are o cheie în plicul închis
 *  al schemei. Absența lui înseamnă că scara există din alt motiv: de regulă e
 *  nucleul clădirii de deasupra, coborât din etaje, care are nevoie de acces în
 *  subsol oricum.
 *
 *  Citit apărat: `props` e punctul de extensie deschis, deci orice poate scrie
 *  acolo orice, iar un panou nu are voie să cadă pe o formă neașteptată. */
function evacDriver(o: CanonicalObject): EvacDriver | undefined {
  const raw = (o.props ?? {})["architecture.evac_critical_point"];
  if (!raw || typeof raw !== "object") return undefined;
  const d = raw as Record<string, unknown>;
  const pt = d.point;
  if (!Array.isArray(pt) || pt.length < 2) return undefined;
  const [x, y] = pt;
  if (typeof x !== "number" || typeof y !== "number") return undefined;
  const num = (v: unknown) => (typeof v === "number" ? v : null);
  // Traseele: numai șiruri de puncte întregi, altfel nimic. `props` e punctul
  // de extensie deschis, deci nu se poate presupune forma.
  const traseu = (v: unknown): number[][] =>
    Array.isArray(v)
      ? v.filter((q): q is number[] => Array.isArray(q) && q.length >= 2
                 && typeof q[0] === "number" && typeof q[1] === "number")
         .map((q) => [q[0], q[1]])
      : [];
  return { point: [x, y], walk_m: num(d.walk_m),
           walk_new_m: num(d.walk_new_m), limit_m: num(d.limit_m),
           tol_m: num(d.tol_m),
           path_new: traseu(d.path_new), path_old: traseu(d.path_old) };
}

// ---------------------------------------------------------------------------
// Normalizarea geometriei: cele două emitere scriu jumătăți diferite
// ---------------------------------------------------------------------------
//
// Un perete, o ușă și un stâlp se pot scrie în două forme, și cele două emitere
// ale platformei au ales-o pe rând pe alta. Măsurat pe câte un document real din
// fiecare (`architecture/canonical.py` cu 1 664 de obiecte, `structure/canonic.py`
// pe p2):
//
//                       GENERAT                        IMPORTAT
//   Wall      axis, thickness_m (des null)   axis, poly, thickness_m, height_m
//   Door      width_m; poziția în `display`  cx, cy, deg, poly, u_m, width_m
//   Window    u_m pe `wall`, width_m         cx, cy, deg, poly, width_m
//   Column    cx, cy, deg, w, h             + poly
//
// `StoreyView` desenează din INEL. Deci un nivel importat se desena întreg, iar
// unul generat ieșea cu camerele și nimic altceva — nu fiindcă lipsea desenul,
// ci fiindcă lipsea jumătatea de geometrie pe care o citește el. Un plan de
// arhitectură venit din generator și unul venit din planșe trebuie să arate la
// fel; aici se închide diferența, într-un singur loc prin care trec amândouă.
//
// Ce se derivă e DETERMINAT, nu ghicit: inelul unui perete iese din axă plus
// grosime, poziția unui gol din axa peretelui gazdă plus `u_m`, inelul unui
// stâlp din centru plus laturi. Singura necunoscută e grosimea pe care
// generatorul o lasă `null` DINADINS (v. `architecture/walls.py`: „WHAT IS NOT
// DECLARED IS NOT INVENTED" — nimeni nu declară grosimea unei fațade, deci
// documentul nu minte că o știe). Pentru DESEN e nevoie totuși de o lățime, iar
// convenția stă mai jos: declarată, pe `kind`, și spusă la inspectare, nu
// strecurată ca măsurătoare.

/** Grosimea cu care se DESENEAZĂ un perete al cărui document n-o declară.
 *
 *  Nu e o măsurătoare și nu intră în document — e lățimea liniei de poché, ca
 *  pe o planșă la 1:100. Pe `kind`, fiindcă `kind` e exact ce deosebește un
 *  perete de fațadă de o compartimentare de 10 cm, iar o singură valoare pentru
 *  amândouă ar desena etajul fie cu pereți de carton, fie cu ziduri de cetate.
 *
 *  Perechea ei din lanțul de calcul e `structure/canonic.py:
 *  GROSIME_PERETE_IMPLICITA_M` (0,40 — peretele structural obișnuit). Aici
 *  valorile sunt mai mici fiindcă întrebarea e alta: acolo se dimensionează,
 *  aici se desenează. */
export const GROSIME_DESEN_M: Readonly<Record<string, number>> = {
  facade: 0.30,
  structural: 0.30,
  party: 0.25,
  partition: 0.10,
  parapet: 0.10,
  fire: 0.25,
};
export const GROSIME_DESEN_IMPLICITA_M = 0.20;

const grosimeDesen = (kind?: string | null) =>
  GROSIME_DESEN_M[kind ?? ""] ?? GROSIME_DESEN_IMPLICITA_M;

/** Dreptunghiul din jurul unui segment, lat `t`. Inelul se închide (primul
 *  punct repetat la sfârșit) fiindcă așa îl scrie importul, iar `StoreyView`
 *  numără vârfurile ca să sară formele degenerate. */
function inelDinAxa(axis: number[][], t: number): number[][] {
  const [[x0, y0], [x1, y1]] = [axis[0], axis[axis.length - 1]];
  const dx = x1 - x0, dy = y1 - y0;
  const L = Math.hypot(dx, dy);
  if (!(L > 0) || !(t > 0)) return [];
  // Normala unitară la axă; `t/2` de fiecare parte.
  const nx = (-dy / L) * (t / 2), ny = (dx / L) * (t / 2);
  return [[x0 + nx, y0 + ny], [x1 + nx, y1 + ny],
          [x1 - nx, y1 - ny], [x0 - nx, y0 - ny],
          [x0 + nx, y0 + ny]];
}

/** Dreptunghiul rotit al unui stâlp: centru, laturi, unghi. */
function inelDinCentru(cx: number, cy: number, w: number, h: number,
                       deg: number): number[][] {
  if (!(w > 0) || !(h > 0)) return [];
  const t = (deg * Math.PI) / 180;
  const c = Math.cos(t), s = Math.sin(t);
  const pt = (u: number, v: number): number[] =>
    [cx + u * c - v * s, cy + u * s + v * c];
  const a = pt(-w / 2, -h / 2), b = pt(w / 2, -h / 2);
  const d = pt(w / 2, h / 2), e = pt(-w / 2, h / 2);
  return [a, b, d, e, a];
}

/** Un perete al nivelului, cu inelul pe care îl desenează planșa.
 *
 *  `presupusa` spune că lățimea poché-ului e convenția de mai sus, nu o cifră
 *  din document — ca inspectorul să nu prezinte drept măsurătoare o valoare pe
 *  care emitentul a refuzat s-o afirme. */
function peretele(o: CanonicalObject): StoreyShape & { presupusa?: boolean } {
  const baza = forma(o);
  if (baza.poly.length >= 3) return baza;
  const axis = (g(o, "axis") ?? []) as number[][];
  if (axis.length < 2) return baza;
  const declarata = g(o, "thickness_m") as number | null | undefined;
  const t = declarata ?? grosimeDesen(o.properties.kind);
  return { ...baza, poly: inelDinAxa(axis, t), thickness_m: t,
           presupusa: declarata == null };
}

/** Un stâlp sau o grindă, cu inelul lui. */
function bara(o: CanonicalObject): StoreyShape {
  const baza = forma(o);
  if (baza.poly.length >= 3) return baza;
  const [cx, cy, w, h] = [g(o, "cx"), g(o, "cy"), g(o, "w"), g(o, "h")];
  if (cx == null || cy == null || w == null || h == null) return baza;
  return { ...baza, poly: inelDinCentru(cx, cy, w, h, g(o, "deg") ?? 0) };
}

/** O rampă, cu inelul pe care îl desenează planșa.
 *
 *  A TREIA emitere care scrie parametric ce cealaltă scrie ca inel — după
 *  perete (axă + grosime) și stâlp (centru + laturi). Rampa generată scrie
 *  `cx, cy, deg, run_m, width_m`; cea care vine cu inel scrie `poly`, `mouth`
 *  și `slope`. Măsurat pe documentul real al proiectului `t<3` (arhitectură v1,
 *  1 566 de obiecte): 8 rampe din 14 fără inel, deci majoritatea.
 *
 *  Fără asta, cele 8 nu se desenau ȘI erau raportate de inventar drept „fără
 *  geometrie" — ceea ce e adevărat despre INEL și fals despre obiect: centrul,
 *  unghiul, lungimea și lățimea determină complet dreptunghiul. Nu se
 *  INVENTEAZĂ nimic; se compune ce documentul deja spune, exact ca la stâlp.
 *
 *  `mouth` și `slope` rămân ce erau: cine le desenează le citește din document,
 *  iar o rampă parametrică pur și simplu nu le are. */
function rampa(o: CanonicalObject): StoreyShape {
  const baza = forma(o);
  if (baza.poly.length >= 3) return baza;
  const [cx, cy] = [g(o, "cx"), g(o, "cy")];
  const [run, lat] = [g(o, "run_m"), g(o, "width_m")];
  if (cx == null || cy == null || run == null || lat == null) return baza;
  // `run` pe direcția lui `deg`, `width` perpendicular — aceeași convenție ca
  // dreptunghiul stâlpului.
  return { ...baza, poly: inelDinCentru(cx, cy, run, lat, g(o, "deg") ?? 0) };
}

/** Un gol, așezat pe peretele lui.
 *
 *  Trei căi, în ordinea în care documentul le oferă:
 *
 *    1. inelul e scris (importul) — se folosește ca atare;
 *    2. `cx/cy/deg` sunt scrise, inelul nu — se face din ele plus `width_m` și
 *       grosimea peretelui gazdă;
 *    3. nimic absolut, dar există `wall` + `u_m` (generatorul) — poziția se
 *       măsoară pe axa peretelui, de la primul capăt.
 *
 *  Golul fără NICIUNA dintre cele trei nu se desenează. Se numără însă, și
 *  numărul iese în `lipsuri`: o ușă generată își poartă poziția în
 *  `display.jambs`, iar `display` e sub-obiectul pe care schema spune explicit
 *  că un cititor are voie să-l ignore — deci ușile generate n-au, în document,
 *  unde să stea. Nu se inventează aici; se spune. */
/** Tâmplăria unui gol, așa cum o scrie fațada pe el
 *  (`props["architecture.opening"].joinery`, v. `fatada.joinery_din_spec`).
 *  Toate cotele în metri; ce lipsește ia convenția machetei. */
export interface TamplarieGol {
  /** Fața văzută a tocului, adâncimea lui și cât stă în spatele finisajului. */
  profil_m?: number;
  adancime_m?: number;
  retragere_m?: number;
  /** Cerceveaua canatului mobil: fața ei, treapta față de toc; bagheta
   *  canatului fix; traversa lată de jos a foii de ușă. */
  cercevea_m?: number;
  treapta_m?: number;
  bagheta_m?: number;
  traversa_usa_m?: number;
  montant_m?: number;
  geam_m?: number;
  /** Ce face fiecare canat, de la începutul golului: `fixed` n-are cercevea
   *  și mâner, restul au. */
  canate: string[];
  /** Lățimile canatelor, în ordinea lui `canate`, când nu sunt egale (ușa:
   *  panourile fixe + foaia de 0,90); lipsă = egale. */
  latimi?: number[];
  culoare?: string;
  /** Mânerul de pe fiecare canat mobil; lipsă = fără. `bar` = bara de tras
   *  verticală a unei uși de intrare, lungă cât `lungime_m`, cu mijlocul la
   *  `inaltime_m`. */
  maner?: { tip: string; culoare?: string; inaltime_m: number; lungime_m?: number };
  /** Pragul ușii; lipsă = fără. */
  prag?: { inaltime_m: number; material?: string };
  /** UȘA DE INTRARE ÎN BLOC (`rol: intrare_bloc`): supralumina de deasupra
   *  canatelor, înaltă cât `supralumina_m`, și interfonul — banda plină de pe
   *  canatul fix `canat`, de la `z_m` peste prag, înaltă cât `inaltime_m`
   *  (`canat` null = pe zidul de lângă ușă, nedesenat). */
  rol?: string;
  supralumina_m?: number;
  interfon?: { canat: number | null; z_m: number; inaltime_m: number; culoare?: string };
}

/** Canatele, profilul și culoarea tocului, când documentul le scrie — și
 *  tâmplăria întreagă, pentru machetă. */
function tamplaria(o: CanonicalObject):
    { canate?: number; profil_m?: number; culoare_toc?: string; tamplarie?: TamplarieGol } {
  const j = (o.props as Record<string, { joinery?: Record<string, unknown> }> | undefined)
    ?.["architecture.opening"]?.joinery;
  if (!j) return {};
  const out: { canate?: number; profil_m?: number; culoare_toc?: string; tamplarie?: TamplarieGol } = {};
  if (typeof j.leaves === "number" && j.leaves >= 1) out.canate = Math.round(j.leaves);
  if (typeof j.frame_profile_m === "number" && j.frame_profile_m > 0) out.profil_m = j.frame_profile_m;
  if (typeof j.colour === "string") out.culoare_toc = j.colour;
  const num = (k: string): number | undefined =>
    typeof j[k] === "number" && (j[k] as number) > 0 ? (j[k] as number) : undefined;
  const n = out.canate ?? 1;
  const moduri = Array.isArray(j.leaf_modes) && j.leaf_modes.length
    ? (j.leaf_modes as unknown[]).map((m) => String(m))
    : Array.from({ length: n }, () => String(j.opening ?? "tilt_turn"));
  const hw = j.hardware as { type?: string; colour?: string; height_m?: number; length_m?: number } | null | undefined;
  const tr = j.transom as { height_m?: number } | null | undefined;
  const ic = j.intercom as { leaf?: number | null; z_m?: number; height_m?: number; colour?: string } | null | undefined;
  const th = j.threshold as { height_m?: number; material?: string } | null | undefined;
  const latimi = Array.isArray(j.leaf_widths_m)
    ? (j.leaf_widths_m as unknown[]).map((v) => Number(v)).filter((v) => v > 0) : [];
  out.tamplarie = {
    ...(latimi.length === moduri.length ? { latimi } : {}),
    profil_m: out.profil_m, adancime_m: num("frame_depth_m"), retragere_m: num("setback_m"),
    cercevea_m: num("sash_m"),
    treapta_m: typeof j.sash_step_m === "number" ? (j.sash_step_m as number) : undefined,
    bagheta_m: num("bead_m"), traversa_usa_m: num("door_rail_m"),
    montant_m: num("mullion_m"), geam_m: num("glass_m"),
    canate: moduri, culoare: out.culoare_toc,
    ...(hw && hw.type && hw.type !== "none"
      ? { maner: { tip: hw.type, culoare: hw.colour, inaltime_m: hw.height_m ?? 1.05,
                   ...(typeof hw.length_m === "number" && hw.length_m > 0 ? { lungime_m: hw.length_m } : {}) } }
      : {}),
    ...(typeof j.rol === "string" ? { rol: j.rol as string } : {}),
    ...(tr && typeof tr.height_m === "number" && tr.height_m > 0 ? { supralumina_m: tr.height_m } : {}),
    ...(ic && typeof ic.z_m === "number" && typeof ic.height_m === "number" && ic.height_m > 0
      ? { interfon: { canat: typeof ic.leaf === "number" ? ic.leaf : null, z_m: ic.z_m,
                      inaltime_m: ic.height_m, culoare: ic.colour } }
      : {}),
    ...(th && typeof th.height_m === "number"
      ? { prag: { inaltime_m: th.height_m, material: th.material } } : {}),
  };
  return out;
}

function golul(o: CanonicalObject, tip: "Door" | "Window",
               pereti: Map<string, StoreyShape>):
    (StoreyShape & { tip: "Door" | "Window" }) | null {
  const op = o.props?.["architecture.opening"] as
    { model?: string; hinge?: string; leaf_m?: number; corner?: { with?: string } } | undefined;
  const baza = {
    ...forma(o), tip,
    ...(typeof op?.corner?.with === "string" ? { colt: op.corner.with } : {}),
    ...(tip === "Door" && op?.model === "double" ? { foi: 2 } : {}),
    ...(tip === "Door" && op?.model === "sidelight" && Number(op.leaf_m) > 0
      ? { foaie_m: Number(op.leaf_m) } : {}),
    ...(tip === "Door" && op?.hinge === "end" ? { balama: "end" as const } : {}),
  };
  const gazda = pereti.get((o.properties.wall ?? "") as string);
  const grosime = baza.thickness_m ?? gazda?.thickness_m
    ?? grosimeDesen(gazda?.kind);
  if (baza.poly.length >= 3) return { ...baza, thickness_m: grosime };

  let { cx, cy, deg } = baza;
  if (cx == null || cy == null) {
    // Calea parametrică: `u_m` de la primul capăt al axei peretelui gazdă.
    const axis = gazda?.axis;
    const u = g(o, "u_m") as number | null | undefined;
    if (!axis || axis.length < 2 || u == null) return null;
    const [[x0, y0], [x1, y1]] = [axis[0], axis[axis.length - 1]];
    const dx = x1 - x0, dy = y1 - y0, L = Math.hypot(dx, dy);
    if (!(L > 0)) return null;
    // `u_m` e distanța până la MIJLOCUL golului: așa îl scrie importul
    // (`de_la_capat` măsurat până la `g["c"]`, centrul), deci așa se citește și
    // aici — altfel ușile generate ar sta decalate cu o jumătate de canat față
    // de cele importate, pe același desen.
    cx = x0 + (dx / L) * u;
    cy = y0 + (dy / L) * u;
    deg = (Math.atan2(dy, dx) * 180) / Math.PI;
  }
  const l = baza.width_m ?? 0;
  if (!(l > 0)) return null;
  return { ...baza, cx, cy, deg: deg ?? 0, thickness_m: grosime,
           poly: inelDinCentru(cx, cy, l, grosime, deg ?? 0) };
}

/** Un `FacadePanel`, cu normala feței lui (de pe `Facade`, în `props`). */
function panoul(o: CanonicalObject, normale: Map<string, number[]>): PanouFatada | null {
  const axis = g(o, "axis") as number[][] | undefined;
  const poly = inel(o);
  const z0 = Number(g(o, "sill_m") ?? 0);
  const h = Number(g(o, "height_m") ?? 0);
  if (!(axis && axis.length >= 2) && poly.length < 3) return null;
  const face = (o.properties.face ?? undefined) as string | undefined;
  return {
    eid: o.id,
    kind: o.properties.kind ?? "",
    layer: String((o.properties as { layer?: string }).layer ?? ""),
    surface: String((o.properties as { surface?: string }).surface ?? "facade"),
    face,
    a: axis?.[0] ?? poly[0],
    b: axis?.[axis.length - 1] ?? poly[1],
    n: face ? normale.get(face) : undefined,
    poly: poly.length >= 3 ? poly : undefined,
    z0, z1: z0 + h,
    depth_m: Number(g(o, "depth_m") ?? 0),
    thickness_m: Number(g(o, "thickness_m") ?? 0),
    material: (o.properties as { material?: string }).material ?? undefined,
    colour: (o.properties as { colour?: string }).colour ?? undefined,
    section: sectiunea(o),
    rosturi: rosturile(o),
    pardoseala: pardoseala(o),
    cortina: cortinaPanoului(o),
    u0: (() => {
      const v = (o.props as Record<string, { u0?: unknown }> | undefined)?.["architecture.panel"]?.u0;
      return typeof v === "number" && Number.isFinite(v) ? v : undefined;
    })(),
  };
}

/** Calepinajul unei PARDOSELI (§26): reperul ca indici în inel, piesele ca
 *  dreptunghiuri `[a0, a1, b0, b1]` sau poligoane `[[a, b], …]`. */
function pardoseala(o: CanonicalObject): Pardoseala | undefined {
  const j = (o.props as Record<string, { joints?: Record<string, unknown> }> | undefined)
    ?.["architecture.panel"]?.joints;
  if (!j || typeof j.floor !== "string" || !Array.isArray(j.tiles)) return undefined;
  const fr = j.frame as { origin?: unknown; along?: unknown } | undefined;
  const al = fr?.along;
  if (!fr || !Number.isInteger(fr.origin) || !Array.isArray(al) || al.length !== 2
      || !al.every((v) => Number.isInteger(v))) return undefined;
  const piese = (j.tiles as unknown[]).filter((t): t is number[] | number[][] =>
    Array.isArray(t) && (t.length === 4 && t.every((v) => Number.isFinite(v))
      || t.length >= 3 && t.every((q) => Array.isArray(q) && q.length === 2 && q.every((v) => Number.isFinite(v)))));
  if (!piese.length) return undefined;
  return {
    fel: j.floor, origine: fr.origin as number, lungul: [al[0] as number, al[1] as number],
    axaLunga: j.long_axis === "b" ? "b" : "a", piese,
    rost: Number(j.width_m ?? 0.004), culoareRost: String(j.colour ?? "#1d1e20"),
    grosime: Number(j.piece_t_m ?? 0.02),
  };
}

/** Grila PERETELUI CORTINĂ de pe un panou `curtain_wall` (v. `layout.cortina`). */
function cortinaPanoului(o: CanonicalObject): CortinaPanou | undefined {
  const c = (o.props as Record<string, { curtain?: Record<string, unknown> }> | undefined)
    ?.["architecture.panel"]?.curtain as {
      mullions_u?: unknown; cells?: unknown; rows_z?: unknown; variant?: string;
      profile?: { width_m?: number; mullion_m?: number; transom_m?: number; cap_m?: number; gap_m?: number };
      glass?: { colour?: string; thickness_m?: number }; opaque?: { colour?: string }; ends_m?: unknown;
      doors?: unknown;
    } | undefined;
  if (!c || !Array.isArray(c.mullions_u) || c.mullions_u.length < 2) return undefined;
  const nr = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? v : d);
  const capete = Array.isArray(c.ends_m) ? c.ends_m : [];
  return {
    montanti: (c.mullions_u as unknown[]).map((v) => nr(v, 0)),
    celule: Array.isArray(c.cells) ? (c.cells as unknown[]).map(String) : [],
    randuri: Array.isArray(c.rows_z) ? (c.rows_z as unknown[]).map((v) => nr(v, 0)) : [0.9, 2.7],
    varianta: String(c.variant ?? "standard"),
    latime: nr(c.profile?.width_m, 0.05), montant: nr(c.profile?.mullion_m, 0.125),
    traversa: nr(c.profile?.transom_m, 0.085), capac: nr(c.profile?.cap_m, 0.02),
    rost: nr(c.profile?.gap_m, 0.02),
    sticla: { culoare: String(c.glass?.colour ?? "#9db4c4"), grosime: nr(c.glass?.thickness_m, 0.044) },
    opac: { culoare: String(c.opaque?.colour ?? "#5a6873") },
    capete: [nr(capete[0], 0), nr(capete[1], 0)],
    ...(Array.isArray(c.doors) ? {
      usi: (c.doors as unknown[]).filter((d): d is number[] => Array.isArray(d) && d.length >= 3)
        // [u0, u1, capul] sau [u0, u1, pragul, capul] (ușa terasei, cu prag)
        .map((d) => d.length >= 4 ? [nr(d[0], 0), nr(d[1], 0), nr(d[2], 0), nr(d[3], 2.1)]
          : [nr(d[0], 0), nr(d[1], 0), nr(d[2], 2.1)]) } : {}),
  };
}

function rosturile(o: CanonicalObject): PanouFatada["rosturi"] {
  const j = (o.props as Record<string, { joints?: { width_m?: number; colour?: string; tiles?: unknown } }> | undefined)
    ?.["architecture.panel"]?.joints;
  if (!j || !Array.isArray(j.tiles) || !j.tiles.length) return undefined;
  if ((j as { floor?: unknown }).floor) return undefined;      // pardoseala are reperul ei (v. `pardoseala`)
  // indicii din document → cei din `placi` (o piesă stricată se sare, iar
  // culorile plăcilor sunt pe indicii documentului)
  const placi: number[][] = [];
  const laNoi = new Map<number, number>();
  (j.tiles as unknown[]).forEach((t, k) => {
    if (Array.isArray(t) && t.length === 4 && t.every((v) => Number.isFinite(v))) {
      laNoi.set(k, placi.length);
      placi.push(t as number[]);
    }
  });
  if (!placi.length) return undefined;
  const accente: Record<number, string> = {};
  for (const a of ((j as { accents?: unknown }).accents as unknown[] | undefined) ?? []) {
    if (!Array.isArray(a) || typeof a[1] !== "string") continue;
    const k = laNoi.get(Number(a[0]));
    if (k !== undefined) accente[k] = a[1];
  }
  return { latime: Number(j.width_m ?? 0.008), culoare: String(j.colour ?? "#1c1d1f"), placi,
           ...(Object.keys(accente).length ? { accente } : {}) };
}

function sectiunea(o: CanonicalObject): number[][] | undefined {
  const s = (o.props as Record<string, { section?: unknown }> | undefined)
    ?.["architecture.panel"]?.section;
  if (!Array.isArray(s) || s.length < 3) return undefined;
  const pts = s.filter((p) => Array.isArray(p) && p.length === 2
    && Number.isFinite(p[0]) && Number.isFinite(p[1])) as number[][];
  return pts.length >= 3 ? pts : undefined;
}

function parapetul(o: CanonicalObject): ParapetFatada | null {
  const axis = g(o, "axis") as number[][] | undefined;
  if (!axis || axis.length < 2) return null;
  return {
    eid: o.id,
    kind: o.properties.kind ?? "glass",
    axis,
    height_m: Number(g(o, "height_m") ?? 1.1),
    sill_m: g(o, "sill_m") != null ? Number(g(o, "sill_m")) : undefined,
    outdoor: ((o.props?.["architecture.railing"] as { outdoor?: string } | undefined)?.outdoor) ?? undefined,
    face: (o.properties.face ?? undefined) as string | undefined,
    material: (o.properties as { material?: string }).material ?? undefined,
    colour: (o.properties as { colour?: string }).colour ?? undefined,
    thickness_m: g(o, "thickness_m") != null ? Number(g(o, "thickness_m")) : undefined,
    ...ecranul(o),
    ...((): { handrail?: ManaCurenta | null } => {
      const m = manaDinProps(o.props?.["architecture.railing"]);
      return m === undefined ? {} : { handrail: m };
    })(),
    ...((): { profil?: ParapetFatada["profil"] } => {
      const ra = o.props?.["architecture.railing"] as Record<string, unknown> | undefined;
      if (!ra || !("base_profile" in ra)) return {};
      const p = ra.base_profile as Record<string, unknown> | null;
      if (!p || typeof p !== "object") return { profil: null };
      const nr = (v: unknown, d: number) => (typeof v === "number" && v > 0 ? v : d);
      return { profil: { width_m: nr(p.width_m, 0.06), height_m: nr(p.height_m, 0.11),
                         material: typeof p.material === "string" ? p.material : undefined,
                         colour: typeof p.colour === "string" ? p.colour : undefined } };
    })(),
    ...(o.properties.kind === "planter"
      ? { planting: (o.props?.["architecture.railing"] as { planting?: string } | undefined)?.planting ?? "mixed" }
      : {}),
  };
}

/** Desenul și golul unui ECRAN FIX (`kind: "screen"`), din props. */
function ecranul(o: CanonicalObject): { pattern?: string; openness?: number } {
  if (o.properties.kind !== "screen") return {};
  const p = o.props?.["architecture.railing"] as { pattern?: string; openness?: number } | undefined;
  return { pattern: p?.pattern ?? "perforated",
           openness: typeof p?.openness === "number" ? p.openness : undefined };
}

/** Normala fiecărei fețe, după id — scrisă de emitent în
 *  `props["architecture.facade"].normal`, ca panoul să știe încotro iese. */
function normaleFete(doc: CanonicalDoc): Map<string, number[]> {
  const out = new Map<string, number[]>();
  for (const o of deTip(doc, "Facade")) {
    const n = (o.props as Record<string, { normal?: unknown }> | undefined)
      ?.["architecture.facade"]?.normal;
    if (Array.isArray(n) && n.length >= 2
        && Number.isFinite(n[0]) && Number.isFinite(n[1])) {
      out.set(o.id, [Number(n[0]), Number(n[1])]);
    }
  }
  return out;
}

/** Fiecare nivel al documentului, de jos în sus, cu obiectele lui.
 *
 *  Include ȘI nivelurile pe care le desenează celelalte două traduceri:
 *  numărătorile `parking`/`flats` sunt cum alege ecranul între desenul de
 *  parcaj, cel de apartamente și cel de arhitectură — o întrebare despre ce E
 *  pe nivel, nu despre cum l-a etichetat cine l-a emis. */
export function toStoreyPlans(doc: CanonicalDoc): StoreyPlan[] {
  const proprietari = (doc.objects ?? [])
    .filter((o) => o.type === "Building" || o.type === "Area");
  const coduri = new Map(proprietari
    .map((o) => [o.id, (o.properties.code ?? "") as string]));
  const amprente = new Map(proprietari
    .map((o) => [o.id, (g(o, "poly") ?? []) as number[][]]));
  // REPERELE nivelurilor subterane — v. `StoreyPlan.refBuildings`. Se calculează
  // O DATĂ, nu per planșeu: sunt aceleași pe toate nivelurile, iar un subsol de
  // două niveluri cu cinci volume deasupra ar fi refăcut lista de zece ori.
  //
  // Numai `Building`: `proprietari` ține și `Area`, iar aria de subsol e chiar
  // nivelul pe care se desenează.
  const refBuildings: Reper[] = deTip(doc, "Building")
    .map((o) => ({ poly: (g(o, "poly") ?? []) as number[][],
                   code: (o.properties.code ?? undefined) as string | undefined }))
    .filter((r) => r.poly.length >= 3);
  // Limita terenului: poligonul lui `Site`, aceeași sursă pe care o citește
  // `toSiteLayer` ca `boundary` — nu `doc.lot`, care pe un plan generat e doar
  // identitate (`{id, name}`), fără contur.
  const site = deTip(doc, "Site")[0];
  const refPlot = (site ? (g(site, "poly") ?? []) : []) as number[][];
  const normale = normaleFete(doc);
  const niveluri = planseeSortate(doc);
  // Câte planșee stau la aceeași cotă: numele lor trebuie să le deosebească,
  // altfel două corpuri dau două butoane „Parter".
  const laCota = new Map<number, number>();
  for (const st of niveluri) {
    const lv = st.properties.level ?? 0;
    laCota.set(lv, (laCota.get(lv) ?? 0) + 1);
  }

  return niveluri.map((st) => {
    const on = onStorey(doc, st.id);
    const level = st.properties.level ?? 0;
    const parking = (on.get("ParkingSpace") ?? []).length;
    const parcare = eParcare(doc, st);
    const cod = coduri.get(ownerOf(st) ?? "") ?? "";
    const label = numeNivel(level, parcare)
      + ((laCota.get(level) ?? 1) > 1 && cod ? ` · ${cod}` : "");
    const cota = g(st, "elevation_m");
    // Pereții ÎNTÂI, indexați pe id: un gol se așază pe axa peretelui lui, deci
    // gazda trebuie să existe (cu inelul și grosimea ei) înainte de goluri.
    const totiPeretii = on.get("Wall") ?? [];
    const pereti = totiPeretii.filter((w) => !pesteSectiune(w)).map(peretele);
    // …iar cei de deasupra tăieturii, separat: planșa nu-i desenează, macheta
    // da (v. `StoreyPlan.wallsAboveCut`).
    const peretiSus = totiPeretii.filter(pesteSectiune).map(peretele);
    const dupaId = new Map(pereti.map((w) => [w.eid, w as StoreyShape]));
    const goluri = [
      ...(on.get("Door") ?? []).map((o) => golul(o, "Door", dupaId)),
      ...(on.get("Window") ?? []).map((o) => golul(o, "Window", dupaId)),
    ];
    return {
      id: st.id,
      level,
      kind: (st.properties.storey_kind ?? "") as string,
      owner: ownerOf(st),
      cod: cod || undefined,
      label,
      cota: typeof cota === "number" && Number.isFinite(cota) ? cota : undefined,
      // Conturul planșeului, iar când nu-l are — al obiectului care îl DEȚINE.
      // Subsolurile generate n-au `Storey.poly` (amprenta e a excavației), deci
      // desenul lor rămânea fără hârtia pe care stă. Nu e o presupunere: e
      // exact ce face și `Vizualizator.conturStructurii` pentru planul de
      // parcaj, mutat aici ca să fie un singur răspuns la „pe ce contur stă
      // nivelul ăsta".
      outline: conturPlanseu(st, amprente),
      parts: placilePlanseului(st, amprente),
      // Numai sub cotă: la etaje conturul clădirii ESTE planșeul, deci reperul
      // n-ar spune nimic — iar lista goală ascunde rândul de strat singură.
      refBuildings: parcare ? refBuildings : [],
      refPlot: parcare ? refPlot : [],
      slabs: (on.get("Slab") ?? []).map(forma),
      stairs: (on.get("Stair") ?? []).map(forma),
      lifts: (on.get("Lift") ?? []).map(forma),
      walls: pereti,
      wallsAboveCut: peretiSus,
      // Golurile care nu s-au putut așeza NU se strecoară ca forme goale:
      // `golul` întoarce null, iar câte au fost se spune în `fara_loc`.
      openings: goluri.filter((o) => o !== null),
      // `bara`, nu `forma`: o parte din spații vin ca dreptunghi rotit
      // (`cx/cy/w/h/deg`) și nu ca inel — pe subsolul generat, 9 din 17 boxe.
      // Fără inel nu se desenau, deși poziția lor era în document.
      spaces: (on.get("Space") ?? []).map((o) => ({
        ...bara(o),
        label: o.display?.label ?? (numeleFelului(o.properties.kind ?? "") || undefined),
        outdoor: (o.props as Record<string, { outdoor?: string }> | undefined)
          ?.["architecture.space"]?.outdoor ?? undefined,
        // zona unei locuințe sacrificate la parter, încă necompartimentată — hol comun
        // în document, dar cu semnul „în lucru" (v. `StoreyView`)
        ...((o.props?.["architecture.recomp"] as { stare?: string } | undefined)?.stare
          ? { recomp: (o.props?.["architecture.recomp"] as { stare?: string }).stare }
          : {}),
      })),
      columns: (on.get("Column") ?? []).map(bara),
      beams: (on.get("Beam") ?? []).map(bara),
      ladders: (on.get("Ladder") ?? []).map(bara),
      // `bara`, nu `forma`: locurile generate vin ca dreptunghi rotit
      // (`cx/cy/w/h/deg`) și n-au inel — toate cele 255 de pe un document real.
      // Cele de la sol, din planul de situație, vin cu `poly`, iar `bara` îl
      // păstrează pe acela. Un singur apel acoperă ambele emiteri.
      // Numărul locului stă în `display.n`, nu în `display.label`, deci `forma`
      // nu-l vede. E singura etichetă pe care o are un loc, și e cea vopsită pe
      // pardoseală — fără ea, desenul nu poate spune «locul 47».
      bays: (on.get("ParkingSpace") ?? []).map((o) => ({
        ...bara(o),
        label: o.display?.n != null ? String(o.display.n) : undefined,
      })),
      aisles: (on.get("AccessibleAisle") ?? []).map(bara),
      // Aleea e o AXĂ, nu o suprafață: lățimea de circulație se pune la desen.
      // `w` vine totuși cu ea: căile de evacuare (`EvacuareTrasee`) deosebesc
      // segmentul de pe alee de cel prin coridor după distanța până la axă.
      lanes: (on.get("Lane") ?? []).map((l) => ({ ...forma(l), width_m: g(l, "w") })),
      // Rampa, prin `rampa`, nu prin `forma`: jumătate din rampele unui document
      // generat n-au inel. V. nota de acolo.
      ramps: (on.get("Ramp") ?? []).map(rampa),
      // Apartamentele și locurile de parcare, ca STRATURI ale aceluiași desen.
      // Erau două vederi alternative (`ApartmentView`, `PlanView`), deci un
      // etaj de apartamente nu-și arăta niciodată pereții și un subsol-parcaj
      // nu-și arăta niciodată compartimentările. Un plan le are pe toate
      // deodată, iar care se vede e alegerea omului, din panoul de straturi.
      flatShapes: (on.get("Flat") ?? []).map((o) => ({
        ...forma(o),
        label: o.display?.label ?? undefined,
        tronson: tronsonulApartamentului(o),
        ...(apartamentulModulului(o) != null ? { apartament: apartamentulModulului(o) } : {}),
        ...((o.props?.["architecture.recomp"] as { stare?: string } | undefined)?.stare
          ? { recomp: (o.props?.["architecture.recomp"] as { stare?: string }).stare }
          : {}),
      })),
      // `bara`, nu `forma`: partea de nucleu e scrisă ca dreptunghi rotit,
      // iar inelul — când există — e același dreptunghi.
      cores: (on.get("CorePart") ?? []).map((o) => ({
        ...bara(o),
        label: o.display?.label ?? (numeleFelului(o.properties.kind ?? "") || undefined),
      })),
      mobilier: (on.get("Furniture") ?? []).flatMap((o): PiesaMobilier[] => {
        const p = (o.props?.["architecture.piesa"] ?? {}) as Partial<PiesaMobilier>;
        const [cx, cy, w, h] = [g(o, "cx"), g(o, "cy"), g(o, "w"), g(o, "h")];
        if (!p.kind || cx == null || cy == null || !(w > 0) || !(h > 0)) return [];
        return [{
          eid: o.id, kind: p.kind, cx, cy, w, h, deg: g(o, "deg") ?? 0,
          ogl: [p.ogl?.[0] === true, p.ogl?.[1] === true],
          ...(p.rang != null ? { rang: p.rang } : {}),
          ...(o.properties.flat ? { flat: o.properties.flat } : {}),
          ...(p.scara?.rampe?.length ? { scara: p.scara } : {}),
        }];
      }),
      parking,
      parcare,
      flats: (on.get("Flat") ?? []).length,
      fara_loc: goluri.filter((o) => o === null).length,
      panouri: (on.get("FacadePanel") ?? []).map((o) => panoul(o, normale))
        .filter((p): p is PanouFatada => p !== null),
      parapete: (on.get("Railing") ?? []).map(parapetul)
        .filter((p): p is ParapetFatada => p !== null),
      nuclee: (on.get("CorePart") ?? []).length,
      bare: (on.get("Bar") ?? []).length,
      mobilate: (on.get("Space") ?? [])
        .filter((o) => MOBILATE.has(o.properties.kind ?? "")).length,
    };
  });
}

// ---------------------------------------------------------------------------
// Originea locală a desenului
// ---------------------------------------------------------------------------
//
// PROBLEMA, măsurată. Geometria documentului e în coordonate proiectate
// ABSOLUTE (Stereo-70: x ≈ 326 000, y ≈ 583 000), iar pânza le înmulțește cu
// scara și le mută cu `translate(tx ty) scale(k)`. Peste 57 px/m produsul trece
// de 2²⁴ ≈ 16,8 milioane — capătul preciziei cu care rasterizorul ține un punct
// — iar translația, de aceeași mărime și de semn opus, taie exact cifrele care
// mai rămăseseră. Rezultatul nu e un desen mai puțin precis, e o PÂNZĂ ALBĂ.
// `view.K_MAX` e 60, deci pragul cădea SUB limita proprie de mărire: omul putea
// mări până acolo, și planul dispărea. Măsurat pe documentul real al lui
// `testiana`: desen corect până la 57 px/m inclusiv, alb de la 58 în sus.
//
// DE CE NU SE REZOLVĂ ÎN COMPONENTE. O transformare imbricată n-ajută: SVG le
// compune într-o singură matrice, deci numerele din `path` rămân mari. Singurul
// lucru care ajută e ca punctele să fie mici, adică scăderea unei origini —
// o singură dată, aici, la marginea în care documentul devine model de desen.
//
// CE NU E. Nu e o schimbare de FORMAT. `crs.origin` există în plic, dar e
// `[0,0,0]` și nu-l citește nimeni; documentul rămâne cum e, fiindcă îl mai
// citesc importerul, validatorul și structura. Originea de aici e o convenție a
// VIZUALIZATORULUI și nu iese din el.


/** Originea desenului: coordonatele documentului, rotunjite în jos la mia de
 *  metri.
 *
 *  Rotunjită, ca să fie STABILĂ: dacă ar fi chiar minimul geometriei, orice
 *  re-solve care mișcă un colț de lot cu 20 cm ar schimba originea, iar
 *  adnotările salvate cu cea veche s-ar desena alături. La mia de metri, tot
 *  ce e pe același lot cade pe aceeași origine. */
export function origineDesen(doc: CanonicalDoc): [number, number] {
  for (const o of doc.objects ?? []) {
    const p = (g(o, "poly") ?? g(o, "axis") ?? g(o, "mouth")) as number[][] | undefined;
    const q = p?.find((v) => Array.isArray(v) && Number.isFinite(v[0]) && Number.isFinite(v[1]));
    if (q) return [Math.floor(q[0] / 1000) * 1000, Math.floor(q[1] / 1000) * 1000];
    const cx = g(o, "cx"), cy = g(o, "cy");
    if (Number.isFinite(cx) && Number.isFinite(cy)) {
      return [Math.floor(cx / 1000) * 1000, Math.floor(cy / 1000) * 1000];
    }
  }
  return [0, 0];
}

/** Deplasează un șir de puncte — sau un șir de șiruri — cu originea. */
function deplaseaza(v: unknown, ox: number, oy: number): unknown {
  if (!Array.isArray(v)) return v;
  if (v.length === 2 && typeof v[0] === "number" && typeof v[1] === "number") {
    return [v[0] - ox, v[1] - oy];
  }
  return v.map((x) => deplaseaza(x, ox, oy));
}

/** Documentul, cu geometria adusă la originea locală.
 *
 *  Copie superficială peste tot în afară de `geometry`, care se rescrie: nimic
 *  din aval nu are voie să vadă documentul original pe jumătate mutat, iar
 *  obiectele în sine (id, properties, display) sunt împărțite fără copiere,
 *  fiindcă nu se ating. */
export function laOrigineLocala(doc: CanonicalDoc, origine: [number, number]): CanonicalDoc {
  const [ox, oy] = origine;
  if (!ox && !oy) return doc;
  return {
    ...doc,
    // Conturul lotului din PLIC, nu doar geometria obiectelor. E singura
    // geometrie care nu stă pe un obiect, iar `Vizualizator` cade pe ea când
    // documentul n-are strat de situație — deci, nedeplasată, ar fi ieșit un lot
    // la 326 km de clădirile lui.
    lot: doc.lot?.boundary
      ? { ...doc.lot, boundary: deplaseaza(doc.lot.boundary, ox, oy) as number[][] }
      : doc.lot,
    objects: (doc.objects ?? []).map((o) => {
      const muta = (p: number[]) => [p[0] - ox, p[1] - oy];
      const props = propsPuncte(o.props, muta);
      const display = afisajPuncte(o.display, muta);
      const geo = o.geometry;
      if (!geo) {
        return props === o.props && display === o.display
          ? o : { ...o, props, ...(display !== o.display ? { display } : {}) };
      }
      const nou: Record<string, unknown> = { ...geo };
      for (const k of POSITION_KEYS) if (geo[k] != null) nou[k] = deplaseaza(geo[k], ox, oy);
      if (typeof geo.cx === "number") nou.cx = geo.cx - ox;
      if (typeof geo.cy === "number") nou.cy = geo.cy - oy;
      return { ...o, geometry: nou as typeof geo, props,
               ...(display !== o.display ? { display } : {}) };
    }),
  };
}

/** Transformarea unui punct din coordonatele documentului în cadrul desenului
 *  — aceeași cu `northUp` + `laOrigineLocala`, pentru geometrii care NU sunt
 *  obiecte ale documentului (diagnostice: harta de depanare, cadrul de film).
 *  Regulile nu se rescriu, se re-aplică: sfertul de tură pe grilele
 *  northing-first (`(a, b) -> (b, -a)`), altfel oglindirea pe y; apoi originea
 *  locală a documentului. */
export function punctInCadru(crs: { epsg?: number | null } | null | undefined,
                             origine: [number, number]): (p: number[]) => number[] {
  const turn = northingFirst(crs as { epsg?: number } | null);
  const [ox, oy] = origine;
  return turn
    ? (p: number[]): number[] => [p[1] - ox, -p[0] - oy]
    : (p: number[]): number[] => [p[0] - ox, -p[1] - oy];
}

/** O adnotare a raportului de verificare, ÎN CADRUL DESENULUI.
 *
 *  Validatorul o scrie în coordonatele documentului (Stereo 70, cu y spre
 *  nord), iar ea nu trece prin traducerile de aici. Până pe 27.10.2026 era doar
 *  DEPLASATĂ la origine, fără `northUp`: pe o grilă northing-first (3844) axele
 *  se întorc un sfert de tură, pe orice altă grilă y se oglindește, deci cota
 *  roșie a tronsonului cădea în afara cadrului, pe partea cealaltă a originii —
 *  previzualizarea din «Verificări» arăta planul fără nicio adnotare. Același
 *  drum ca pentru diagnostice: `punctInCadru`, pe fiecare cheie cu puncte. */
export function adnotareInCadru<T extends object>(
  anno: T, crs: { epsg?: number | null } | null | undefined, origine: [number, number],
): T {
  const pt = punctInCadru(crs, origine);
  const mapeaza = (v: unknown): unknown => {
    if (!Array.isArray(v)) return v;
    if (v.length === 2 && typeof v[0] === "number" && typeof v[1] === "number") return pt(v);
    return v.map(mapeaza);
  };
  const src = anno as unknown as Record<string, unknown>;
  const nou: Record<string, unknown> = { ...src };
  for (const k of ["a", "b", "pts", "poly", "segs", "region"]) {
    if (src[k] != null) nou[k] = mapeaza(src[k]);
  }
  return nou as T;
}

// ---------------------------------------------------------------------------
// Inventarul documentului: ce ține JSON-ul și ce ajunge pe desen
// ---------------------------------------------------------------------------
//
// ÎNTREBAREA, a Oanei (04.09.2026): «cred ca ar ajuta sa vedem tot ce e in json
// in UI ca sa ne dam seama ca nu e junk nicaieri» — pusă după ce a găsit rampe
// care stau în afara subsolului. Din ecran răspunsul nu se putea da. `nivelul`
// trece documentul prin vocabularul pe care planșa îl desenează, iar ce nu e în
// vocabular — sau ce e, dar n-are inel, sau atârnă de un planșeu care nu
// există — dispare fără urmă ȘI FĂRĂ NUMĂR. Un desen care tace despre ce n-a
// desenat nu poate fi folosit ca să verifici că generatorul n-a scăpat gunoi:
// lipsa arată la fel ca absența.
//
// CE NU E. Nu e a doua listă de tipuri desenabile. Exact aia ar fi divergat —
// cine adaugă mâine un tip în `nivelul` nu l-ar fi adăugat și aici, iar panoul
// ar fi raportat drept «nedesenat» ceva ce se desenează. Ce SE desenează se
// citește din planurile deja construite: `forma` pune pe fiecare formă `eid`-ul,
// care e chiar `id`-ul obiectului, deci inventarul e SCĂDEREA desenului din
// document și nu poate rămâne în urma lui.
//
// DE CE PE TOT DOCUMENTUL, nu pe nivelul curent. Întrebarea nu e «ce se vede
// acum», ci «are documentul obiecte pe care vizualizatorul nu le arată
// niciodată». Un `Ramp` numărat drept nedesenat fiindcă te uiți la etajul 3 ar
// fi un fals pozitiv la fiecare schimbare de cotă.

/** De ce un obiect al documentului nu ajunge pe niciun desen.
 *
 *  Ordinea în care se pun contează, fiindcă un obiect poate cădea sub mai
 *  multe: se raportează cel mai ACȚIONABIL. O referință de planșeu care nu duce
 *  nicăieri e o greșeală a emitentului și trece înaintea oricărei explicații
 *  care ar face-o să pară normală. */
export type MotivNedesenat =
  /** `properties.storey` arată către un id pe care niciun `Storey` nu-l are.
   *  Obiectul nu e pe niciun nivel, deci nu se desenează nicăieri — și ăsta e
   *  BUG-ul pentru care panoul există. */
  | "planseu_inexistent"
  /** N-are `storey` deloc. Formatul spune că un obiect are ori un planșeu, ori
   *  situl (v. `canonical.test.ts`, «gives every object an address»), deci ăsta
   *  se adresează sitului: îl desenează planul de situație, altă planșă. Nu e o
   *  lipsă. */
  | "pe_situatie"
  /** E pe un planșeu real, dar planul n-are vocabular pentru tipul lui. */
  | "tip_nedesenat"
  /** FAȚADA, care are planșa ei. `Facade`, `FacadePanel` și `Railing` sunt
   *  straturile de pe fețele corpului — termoizolație, tencuială, placaj, brâu,
   *  balustradă — și se văd în 3D și în elevație, nu în planul de arhitectură.
   *
   *  Ca `pe_situatie`: NU e o lipsă, e altă planșă. Fără deosebirea asta ele
   *  cădeau în `tip_nedesenat` și stratul de fantome le punea PESTE plan —
   *  măsurat pe ultimul etaj, 18 panouri de acoperiș acopereau 6 588 m², adică
   *  96% din amprentă, iar planul de dedesubt nu se mai vedea deloc. */
  | "pe_fatada"
  /** ÎNCEPE DEASUPRA TĂIETURII. Planul de arhitectură e o secțiune orizontală
   *  la `SECTIUNE_M`; un element al cărui prag stă peste ea nu e tăiat de
   *  planșă, deci nu se desenează în ea — se vede în 3D și în elevație.
   *
   *  Cazul care a cerut regula: aticul acoperișului. `emit._acoperis` îl scrie
   *  ca `Wall` de fel `parapet` pe ULTIMUL etaj, cu `sill_m` la înălțimea
   *  nivelului (3,00 m), fiindcă acolo stă în clădire — dar în PLANUL acelui
   *  etaj el e un inel de 774-1 483 m² brut care se desenează peste tot ce e
   *  dedesubt. Măsurat pe documentul beneficiarului: etajul 11 ieșea alb, cu
   *  uși, ferestre și cifre de legendă plutind deasupra, fiindcă ele se
   *  desenează DUPĂ pereți, iar terasele se vedeau doar fiindcă tocmai ele
   *  fuseseră scăzute din conturul acoperișului.
   *
   *  Gaura inelului (`holes`) repară culoarea, nu greșeala: elementul tot nu e
   *  în secțiune. Ca `pe_situatie` și `pe_fatada`, nu e o lipsă — e altă
   *  planșă. */
  | "peste_sectiune"
  /** Tip ORGANIZATORIC: grupează alte obiecte, nu e geometrie de planșă.
   *
   *  `Tronson` e cazul — 24 pe parterul unui document real — și a stat până
   *  acum în lista de reparat, sub «tip nedesenat de plan». Dar un tronson e o
   *  bucată de clădire între rosturi: spune despre CE FAC alte obiecte
   *  împreună, exact ca `Storey`, care e sărit din inventar din același motiv.
   *  Un plan care l-ar desena ar desena un dreptunghi peste apartamentele
   *  dinăuntru.
   *
   *  Rămâne NUMĂRAT — „vedem tot ce e în json" cere să apară la «Arată tot» —
   *  dar nu mai stă în lista de probleme. Dacă cineva îl desenează cândva,
   *  scoate tipul din `TIPURI_ORGANIZATORICE` și rândul se întoarce singur. */
  | "tip_organizatoric"
  /** E în model, dar fără inel de cel puțin 3 vârfuri — `StoreyView` sare
   *  formele degenerate, deci obiectul e „desenat" în nimic. */
  | "fara_geometrie";

const MOTIV_CHEIE: Readonly<Record<MotivNedesenat, Cheie>> = {
  planseu_inexistent: "motiv.planseu_inexistent",
  pe_situatie: "motiv.pe_situatie",
  tip_nedesenat: "motiv.tip_nedesenat",
  pe_fatada: "motiv.pe_fatada",
  peste_sectiune: "motiv.peste_sectiune",
  tip_organizatoric: "motiv.tip_organizatoric",
  fara_geometrie: "motiv.fara_geometrie",
};

/** De ce n-a fost desenat obiectul, în limba ecranului. */
export const numeleMotivului = (m: MotivNedesenat): string => t(MOTIV_CHEIE[m]);

/** Tipurile care GRUPEAZĂ alte obiecte — v. `tip_organizatoric`. */
const TIPURI_ORGANIZATORICE = new Set<string>(["Tronson"]);

/** Tipurile fațadei — v. `pe_fatada`. */
const TIPURI_DE_FATADA = new Set<string>(["Facade", "FacadePanel", "Railing"]);

/** Înălțimea tăieturii planului, în metri — convenția planșei de arhitectură.
 *  Peste ea nu se secționează; sub ea, da (un parapet de balcon are pragul 0). */
const SECTIUNE_M = 1.2;

/** Elementul începe deasupra tăieturii? V. `peste_sectiune`.
 *
 *  Numai pereții: un gol își poartă pragul ca să se deseneze corect ÎN peretele
 *  lui (glaf, alonjă), nu ca să fie scos din planșă. */
const pesteSectiune = (o: CanonicalObject): boolean =>
  o.type === "Wall" && Number(g(o, "sill_m") ?? 0) >= SECTIUNE_M;

/** Un rând al inventarului: un `(tip, kind)`, cu câte are documentul și câte
 *  ajung pe desen. */
export interface RandInventar {
  type: ObjectType;
  /** `properties.kind`, sau "" pentru tipurile care n-au niciunul. */
  kind: string;
  /** Numele românesc, din catalogul generat. Pentru un tip fără kind se caută
   *  pe numele tipului cu literă mică — `Bar` -> «Segment de bară» — fiindcă
   *  exact acolo își ține catalogul rândul lor (v. nota din `kinds.py`). */
  nume: string;
  total: number;
  desenate: number;
  /** Câte NU se desenează, pe motive. Cheile absente sunt zero. */
  motive: Partial<Record<MotivNedesenat, number>>;
  /** Planșeele pe care stă rândul, în două feluri — fiindcă panoul pune două
   *  întrebări diferite:
   *
   *    `planse`      — unde are rândul OBIECTE, oricare. Răspunde la «ce e pe
   *                    nivelul ăsta», adică la «Arată tot».
   *    `planseLipsa` — unde are obiecte NEDESENATE. Răspunde la «ce am de
   *                    reparat AICI».
   *
   *  Două, nu unul: cu doar cel de-al doilea, «Arată tot» pe un nivel sănătos
   *  întorcea UN SINGUR rând — cel cu lipsuri — și ascundea tot ce merge, adică
   *  fix pe dos decât spune butonul. Cu doar primul, lista de reparat ar fi
   *  adus rânduri a căror problemă e pe alt nivel. */
  planse: string[];
  planseLipsa: string[];
}

/** Un obiect nedesenat care TOTUȘI are un inel: se poate arăta ca fantomă peste
 *  plan, ca să se vadă unde stă. Aici e rampa Oanei. */
export interface Fantoma extends StoreyShape {
  type: ObjectType;
  motiv: MotivNedesenat;
  /** Planșeul de care zice că se agață. Pentru `planseu_inexistent` e chiar
   *  id-ul care nu duce nicăieri — cel pe care omul îl caută în JSON. */
  storey?: string;
}

export interface Inventar {
  randuri: RandInventar[];
  total: number;
  desenate: number;
  nedesenate: number;
  fantome: Fantoma[];
  /** Aceleași două cifre, dar PE PLANȘEU, ca panoul să poată spune cât are
   *  nivelul la care te uiți. Documentul întreg arăta 23 635/23 815 pe orice
   *  etaj — o cifră care nu se schimbă când schimbi nivelul nu vorbește despre
   *  nivel. Cheia e id-ul planșeului; obiectele sitului n-au niciunul și nu
   *  intră. */
  peNivel: Record<string, { total: number; desenate: number }>;
}

/** Forma unui obiect OARECARE, cu aceiași normalizatori pe care îi folosește
 *  planul.
 *
 *  Trebuie să fie ACEIAȘI: o fantomă desenată altfel decât ar fi fost desenat
 *  obiectul „adevărat" ar muta discuția de la «de ce nu se vede» la «de ce se
 *  vede așa». `peretele` scoate inelul din axă plus grosime, `bara` din centru
 *  plus laturi, iar restul își poartă inelul. */
function formaOricui(o: CanonicalObject): StoreyShape {
  if (o.type === "Wall") return peretele(o);
  if (o.type === "Column" || o.type === "Beam" || o.type === "Space"
      || o.type === "ParkingSpace" || o.type === "AccessibleAisle") {
    return bara(o);
  }
  const baza = forma(o);
  if (baza.poly.length >= 3) return baza;
  // Rampa scrisă de generator n-are inel: are centru, pantă și lățime, iar
  // banda o compune desenul (`glyphs.RampIntView`: un `rect` de `run` pe `w`,
  // rotit cu `deg` în jurul centrului). Aceleași trei numere, aceeași bandă —
  // altfel exact obiectul de la care a pornit tot, rampa din afara subsolului,
  // ar fi fost numărată și n-ar fi putut fi ARĂTATĂ.
  if (o.type === "Ramp") {
    const [cx, cy] = [g(o, "cx"), g(o, "cy")];
    const [run, lat] = [g(o, "run_m"), g(o, "width_m")];
    if (cx != null && cy != null && run != null && lat != null) {
      return { ...baza, poly: inelDinCentru(cx, cy, run, lat, g(o, "deg") ?? 0) };
    }
  }
  return baza;
}

/** Id-urile pe care desenul le-a consumat.
 *
 *  DOUĂ PREDICATE, fiindcă vizualizatorul are trei randoare și ele nu desenează
 *  din aceeași geometrie:
 *
 *    `dinInel`  — familiile lui `StoreyView`. Acolo desenul CHIAR pleacă de la
 *                 un inel, iar unul sub 3 vârfuri e sărit la desenare: forma
 *                 intră în model și nu se vede. Diferența asta E `fara_geometrie`.
 *
 *    `desenat`  — familiile desenate din altceva decât un inel: aleea din axă,
 *                 ușa din tocuri, locul de parcare din centru plus mărimea pe
 *                 care o dă norma (2,50 x 5,00, pusă de `toPlan` — documentul
 *                 n-o scrie), rampa din centru plus pantă. Acolo „are inel" nu e
 *                 întrebarea potrivită, iar modelul de desen nici nu se
 *                 construiește dacă lipsește ce-i trebuie: prezența LUI e
 *                 răspunsul.
 *
 *  Fără despărțirea asta, inventarul raporta 400 de uși, 37 de locuri, 13 alei
 *  și 2 rampe drept nedesenate pe un document care le desenează pe toate — o
 *  alarmă falsă mai rea decât tăcerea de dinainte. */
function eiduriDesenate(niveluri: StoreyPlan[], etaje: ApartmentLevel[],
                        planuri: SolvedPlan[]) {
  const inModel = new Set<string>();
  const cuInel = new Set<string>();
  const dinInel = (eid: string | undefined, poly: number[][] | undefined) => {
    if (!eid) return;
    inModel.add(eid);
    if ((poly?.length ?? 0) >= 3) cuInel.add(eid);
  };
  const desenat = (eid: string | undefined) => {
    if (!eid) return;
    inModel.add(eid);
    cuInel.add(eid);
  };

  for (const n of niveluri) {
    // `n.cores` stă în listă fiindcă `StoreyView` îl DESENEAZĂ — partea de
    // nucleu vine ca `CorePart` și se desenează ca o cameră. Calea prin
    // `etaje` de mai jos n-o acoperă: un `ApartmentLevel` se construiește doar
    // pentru planșeele CU apartamente, deci pe un subsol sau pe un etaj tehnic
    // aceleași obiecte ar fi ieșit «nedesenate» — cu fantomă cu tot — pentru
    // ceva ce se vede pe plan.
    for (const familie of [n.slabs, n.walls, n.openings, n.spaces, n.cores,
                           n.columns, n.beams, n.ladders ?? [], n.stairs ?? [], n.lifts ?? [],
                           n.bays, n.lanes,
                           n.ramps, n.aisles, n.flatShapes]) {
      for (const s of familie) dinInel(s.eid, s.poly);
    }
    // Balustradele: o polilinie, nu un inel — dar planul le desenează.
    for (const r of n.parapete ?? []) desenat(r.eid);
  }

  // Straturile desenate de `ApartmentView`, nu de `StoreyView`: nucleele,
  // barele, corpurile, balcoanele și ușile. Fără ele, cele 40 de `CorePart` și
  // cele 8 `Bar` ale documentului real ar fi raportate drept «tip nedesenat» —
  // ceea ce ar trimite pe cineva să caute un bug într-un strat care merge.
  for (const e of etaje) {
    for (const f of e.flats) {
      dinInel(f.eid, f.poly);
      if (f.balcony) dinInel(f.balcony.eid, f.balcony.poly);
      // Ușa se desenează din tocuri, balamale și vârf, nu dintr-un inel.
      for (const u of f.doors ?? []) desenat(u.eid);
    }
    for (const b of e.bars) dinInel(b.eid, b.poly);
    for (const c of e.cores) {
      // Nucleul n-are inel propriu — e un centru cu părți — deci se socotește
      // desenat PRIN părțile lui. Altfel ar fi apărut veșnic drept „fără
      // geometrie", pentru un obiect care se vede pe plan.
      for (const p of c.parts) dinInel(p.eid, p.poly);
      if (c.eid && c.parts.some((p) => (p.poly?.length ?? 0) >= 3)) desenat(c.eid);
      else inModel.add(c.eid ?? "");
    }
    for (const b of e.buildings ?? []) dinInel(b.eid, b.poly);
  }

  // Planșa de parcaj (`PlanView`). E al treilea randor și desenează exact
  // vocabularul pe care documentul îl scrie fără inel.
  for (const pl of planuri) {
    for (const sp of pl.spaces) desenat(sp.id);
    for (const l of pl.lanes) desenat(l.eid);
    if (pl.rampInt) desenat(pl.rampInt.eid);
  }
  return { inModel, cuInel };
}

/** Fantomele care au ce căuta peste planșeele desenate acum.
 *
 *  DOUĂ FELURI DE OBIECT NEDESENAT, și abia asta le desparte:
 *
 *    Unul ARE un planșeu adevărat și tot nu se desenează — o rampă al cărei
 *    inel e gol, un tip pe care planul nu-l cunoaște. Locul lui e ȘTIUT, deci
 *    se arată acolo și nicăieri altundeva. Puse pe toate nivelurile, spuneau o
 *    minciună: pe un document real, patru rampe de pe patru planșee diferite se
 *    desenau toate peste subsolul −3, adică trei din patru într-un loc în care
 *    nu sunt.
 *
 *    Celălalt n-are unde să fie — `planseu_inexistent`. Ăla se arată pe orice
 *    nivel, fiindcă nu există unul „al lui" pe care să-l aștepți; ținut să-și
 *    aștepte planșeul, n-ar apărea niciodată, iar el e chiar cazul de la care a
 *    pornit tot. */
export function fantomeDePus(fantome: Fantoma[],
                             planseeActive: Iterable<string>): Fantoma[] {
  const active = new Set(planseeActive);
  return fantome.filter((f) => f.motiv === "planseu_inexistent"
                            || !f.storey || active.has(f.storey));
}

/** Ce ține documentul, ce ajunge pe desen, și de ce restul nu ajunge.
 *
 *  `niveluri`, `etaje` și `planuri` sunt CHIAR modelele desenate, câte unul de
 *  fiecare randor — se dau din afară, nu se recalculează aici, ca inventarul să
 *  vorbească despre desenul de pe ecran și nu despre unul pe care l-ar construi
 *  singur. */
export function inventarDocument(doc: CanonicalDoc, niveluri: StoreyPlan[],
                                 etaje: ApartmentLevel[] = [],
                                 planuri: SolvedPlan[] = []): Inventar {
  const { inModel, cuInel } = eiduriDesenate(niveluri, etaje, planuri);
  const planse = new Set(planseeSortate(doc).map((s) => s.id));
  const randuri = new Map<string, RandInventar>();
  // Ce planșee are deja fiecare rând în `planse` / `planseLipsa` — aceeași
  // întrebare ca `includes` (SameValueZero), fără să parcurgă lista la fiecare obiect.
  const dejaPlanse = new Map<RandInventar, { planse: Set<unknown>; lipsa: Set<unknown> }>();
  const fantome: Fantoma[] = [];
  const peNivel: Record<string, { total: number; desenate: number }> = {};
  let desenate = 0;

  for (const o of doc.objects ?? []) {
    // Planșeul e HÂRTIA, nu un lucru desenat pe ea: conturul lui E planul.
    // Numărat ca obiect nedesenat, ar fi cerut o explicație pentru fiecare
    // nivel al documentului.
    if (o.type === "Storey") continue;
    const kind = (o.properties.kind ?? "") as string;
    const cheie = `${o.type} ${kind}`;
    let rand = randuri.get(cheie);
    if (!rand) {
      rand = { type: o.type, kind,
               nume: numeleFelului(kind) || numeleFelului(o.type.toLowerCase())
                     || o.type,
               total: 0, desenate: 0, motive: {},
               planse: [], planseLipsa: [] };
      randuri.set(cheie, rand);
      dejaPlanse.set(rand, { planse: new Set(), lipsa: new Set() });
    }
    const deja = dejaPlanse.get(rand)!;
    rand.total++;
    const peStoreyId = (o.properties.storey ?? "") as string;
    if (peStoreyId) {
      const c = peNivel[peStoreyId] ?? (peNivel[peStoreyId] = { total: 0, desenate: 0 });
      c.total++;
      if (cuInel.has(o.id)) c.desenate++;
      if (!deja.planse.has(peStoreyId)) { deja.planse.add(peStoreyId); rand.planse.push(peStoreyId); }
    }

    if (cuInel.has(o.id)) { rand.desenate++; desenate++; continue; }

    const st = (o.properties.storey ?? "") as string;
    const motiv: MotivNedesenat =
      !st ? "pe_situatie"
      : !planse.has(st) ? "planseu_inexistent"
      : inModel.has(o.id) ? "fara_geometrie"
      : TIPURI_DE_FATADA.has(o.type) ? "pe_fatada"
      : pesteSectiune(o) ? "peste_sectiune"
      : TIPURI_ORGANIZATORICE.has(o.type) ? "tip_organizatoric"
      : "tip_nedesenat";
    rand.motive[motiv] = (rand.motive[motiv] ?? 0) + 1;
    // Planșeul pe care stă LIPSA — v. `planseLipsa`.
    if (st && !deja.lipsa.has(st)) { deja.lipsa.add(st); rand.planseLipsa.push(st); }

    // FANTOMELE: ce se poate desena, din tot ce nu se desenează.
    //
    // `pe_situatie` nu intră — un drum peste planul etajului 3 e zgomot, și are
    // planșa lui. Restul intră, INCLUSIV `fara_geometrie`, și tocmai ăla e
    // cazul care plătește: pe un document real, 4 rampe din 8 intră în plan cu
    // inelul gol și sunt sărite la desenare, deși `formaOricui` le poate
    // compune banda din centru, pantă și lățime. Fantoma nu repară desenul — o
    // arată ca întrebare, care e exact ce e.
    if (motiv !== "pe_situatie" && motiv !== "pe_fatada"
        && motiv !== "peste_sectiune") {
      const s = formaOricui(o);
      if (s.poly.length >= 3) {
        fantome.push({ ...s, type: o.type, motiv, storey: st || undefined });
      }
    }
  }

  const lista = [...randuri.values()].sort((a, b) =>
    // Întâi ce nu se desenează, fiindcă asta există panoul să arate; apoi
    // descrescător după cât de mult, ca rândul cel mai greu să fie primul.
    ((b.total - b.desenate) - (a.total - a.desenate))
    || (b.total - a.total) || a.type.localeCompare(b.type));
  const total = lista.reduce((t, r) => t + r.total, 0);
  return { randuri: lista, total, desenate, nedesenate: total - desenate,
           fantome, peNivel };
}
